import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { formatCurrency } from '../utils/format';
import { sortNewestFirst } from '../utils/newestFirst';
import {
  extractDailyWindowFromText,
  formatDailyHoursLabel,
  formatPromotionDealLabel,
} from '../utils/promotionSchedule';
import {
  ensurePinnedOrderPresent,
  isActionableOrderStatus,
  mergePinnedIntoActiveOrders,
} from '../utils/pinActiveOrder';
import { useToast } from '../context/ToastContext';
import { useLivePoll } from '../hooks/useLivePoll';
import { alertOnNewPendingOrders } from '../utils/baristaOrderAlert';
import ConfirmDialog from '../components/ConfirmDialog';
import OrderOperationsPanel, { type HistoryFilter } from '../components/OrderOperationsPanel';
import OrderIssuesPanel from '../components/OrderIssuesPanel';
import BusinessReportsPanel from '../components/BusinessReportsPanel';
import DineInDiscountCard from '../components/DineInDiscountCard';
import DineInReportPanel from '../components/DineInReportPanel';
import ScanDineIn from '../components/ScanDineIn';
import PerformanceHealthPanel, {
  PERFORMANCE_NAV_ITEMS,
  type PerformanceSectionId,
} from '../components/PerformanceHealthPanel';
import CustomerInsightsPanel, {
  CUSTOMER_NAV_ITEMS,
  type CustomerSectionId,
} from '../components/CustomerInsightsPanel';
import PaymentsInfoPanel, {
  PAYMENTS_NAV_ITEMS,
  type PaymentSectionId,
} from '../components/PaymentsInfoPanel';
import UsersInfoPanel from '../components/UsersInfoPanel';
import StoreHoursSettingsPanel, {
  SETTINGS_NAV_ITEMS,
  type SettingsSectionId,
} from '../components/StoreHoursSettingsPanel';
import { normalizeStoreHours, type StoreHoursConfig } from '../utils/storeHours';
import { getRangeBounds } from '../utils/reportRange';
import { getOrderStatusDisplay } from '../utils/orderStatusLabel';
import DeliveryOrderSummary from '../components/DeliveryOrderSummary';
import MenuCustomizationPicker from '../components/MenuCustomizationPicker';
import MenuPriceFields from '../components/MenuPriceFields';
import {
  cafePriceFromCustomerTotal,
  customerTotalFromCafePrice,
  DEFAULT_MENU_PLATFORM_FEE_PCT,
} from '../utils/menuPlatformPricing';
import {
  MenuItemCustomizationConfig,
  configFromApiItem,
  formatCustomizationConfigSummary,
  suggestCustomizationConfig,
} from '../config/drinkCustomizations';
import { FaBars, FaClipboardList, FaUtensils, FaBullhorn, FaHome, FaStore, FaChartBar, FaUsers, FaFileAlt, FaWallet, FaCog, FaClock, FaEdit, FaTimes, FaUserFriends, FaExclamationTriangle } from 'react-icons/fa';
import { qhShell } from './baristaTheme';
import packageJson from '../../package.json';
import { formatPaymentMethodLabel } from '../utils/paymentMethodLabels';
import {
  getBaristaProfileRawSync,
  getBaristaTokenSync,
  setBaristaProfile,
  setBaristaToken,
} from '../utils/authStorage';

function QhNavIcon({ active, children }: { active: boolean; children: React.ReactNode }) {
  return <span className={qhShell.navIconBox(active)}>{children}</span>;
}

type MarketingSectionId =
  | 'campaigns-offers'
  | 'performance-tracking'
  | 'campaign-management';
type PromotionListFilter = 'all' | 'active' | 'campaign' | 'offer';

const MARKETING_NAV_ITEMS: { id: MarketingSectionId; label: string }[] = [
  { id: 'campaigns-offers', label: 'Campaigns & offers' },
  { id: 'performance-tracking', label: 'Performance tracking' },
  { id: 'campaign-management', label: 'Campaign management' },
];

function scrollToPageTarget(elementId: string) {
  requestAnimationFrame(() => {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-lg');
    window.setTimeout(() => {
      el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-lg');
    }, 2000);
  });
}

function MarketingJumpLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left text-amber-800 underline decoration-amber-400/70 underline-offset-2 hover:text-amber-950 hover:decoration-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm"
    >
      {children}
    </button>
  );
}

interface OrderItem {
  menu_item_id: string;
  quantity: number;
  name?: string;
  note?: string;
  customizations?: Record<string, string>;
}

interface Order {
  id: string;
  order_number?: string;
  cafe_id: string;
  cafe_name: string;
  cafe_address: string;
  user_name: string;
  user_email: string;
  user_phone?: string | null;
  user_id?: string;
  items: OrderItem[];
  total_amount: number;
  points_earned: number;
  points_used: number;
  student_discount?: number;
  status: string;
  name?: string;
  payment_method?: string;
  created_at: string;
  fulfillment_type?: string | null;
  delivery_address?: string | null;
  delivery_distance_km?: number | null;
}

function splitDateTimeLocal(value: string) {
  if (!value) return { date: '', time: '' };
  const [date, timePart] = value.split('T');
  return { date: date || '', time: (timePart || '').slice(0, 5) };
}

function joinDateTimeLocal(date: string, time: string, fallbackTime: string) {
  if (!date) return '';
  return `${date}T${time || fallbackTime}`;
}

type PromoFormState = {
  promotion_type: 'campaign' | 'offer';
  offer_tier: 'basic' | 'highlighted';
  title: string;
  description: string;
  deal: 'percent' | 'bogo_100' | 'bogo_50' | 'bogo_25';
  campaign_audience: 'all' | 'new' | 'returning';
  campaign_placement: 'feed' | 'homepage';
  offer_applies_to: 'drinks' | 'food' | 'all';
  discount_percentage: number;
  setup_fee_monthly: number;
  budget_total_jod: number;
  performance_addon_enabled: boolean;
  performance_fee_per_redeemed_order: number;
  valid_from: string;
  valid_until: string;
  restrict_daily_hours: boolean;
  daily_start_time: string;
  daily_end_time: string;
};

function emptyPromoForm(type: 'campaign' | 'offer' = 'campaign'): PromoFormState {
  return {
    promotion_type: type,
    offer_tier: 'basic',
    title: '',
    description: '',
    deal: 'percent',
    campaign_audience: 'all',
    campaign_placement: 'feed',
    offer_applies_to: 'drinks',
    discount_percentage: 10,
    setup_fee_monthly: 7,
    budget_total_jod: 0,
    performance_addon_enabled: false,
    performance_fee_per_redeemed_order: 0.04,
    valid_from: '',
    valid_until: '',
    restrict_daily_hours: false,
    daily_start_time: '08:00',
    daily_end_time: '10:00',
  };
}

function toDateTimeLocalValue(raw: string | null | undefined): string {
  if (!raw) return '';
  const value = String(raw);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value.slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function promoFormFromRow(promo: any): PromoFormState {
  const isOffer = String(promo?.promotion_type || '') === 'offer';
  const off = Number(promo?.bogo_off_percent);
  const deal: PromoFormState['deal'] =
    String(promo?.deal_type || '').toLowerCase() === 'bogo'
      ? off === 50
        ? 'bogo_50'
        : off === 25
          ? 'bogo_25'
          : 'bogo_100'
      : 'percent';
  const audience = String(promo?.campaign_audience || 'all');
  const placement = String(promo?.campaign_placement || 'feed');
  const applies = String(promo?.offer_applies_to || 'drinks');
  let start = String(promo?.daily_start_time || '').trim();
  let end = String(promo?.daily_end_time || '').trim();
  if (!start || !end) {
    const extracted = extractDailyWindowFromText(
      `${promo?.title || ''} ${promo?.description || ''}`
    );
    if (extracted) {
      start = extracted.start;
      end = extracted.end;
    }
  }
  return {
    promotion_type: isOffer ? 'offer' : 'campaign',
    offer_tier: String(promo?.offer_tier || 'basic') === 'highlighted' ? 'highlighted' : 'basic',
    title: String(promo?.title || ''),
    description: String(promo?.description || ''),
    deal,
    campaign_audience: audience === 'new' || audience === 'returning' ? audience : 'all',
    campaign_placement: placement === 'homepage' ? 'homepage' : 'feed',
    offer_applies_to: applies === 'food' || applies === 'all' ? applies : 'drinks',
    discount_percentage: Number(promo?.discount_percentage || 10),
    setup_fee_monthly: Number(promo?.setup_fee_monthly || (isOffer ? 7 : 0)),
    budget_total_jod: Number(promo?.budget_total_jod || 0),
    performance_addon_enabled: Number(promo?.performance_addon_enabled || 0) === 1,
    performance_fee_per_redeemed_order: Number(promo?.performance_fee_per_redeemed_order || 0.04),
    valid_from: toDateTimeLocalValue(promo?.valid_from),
    valid_until: toDateTimeLocalValue(promo?.valid_until),
    restrict_daily_hours: Boolean(start && end),
    daily_start_time: start || '08:00',
    daily_end_time: end || '10:00',
  };
}

function DateTimeSplitInput({
  value,
  onChange,
  fallbackTime,
  dateLabel,
  timeLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  fallbackTime: string;
  dateLabel: string;
  timeLabel: string;
}) {
  const { date, time } = splitDateTimeLocal(value);
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">{dateLabel}</label>
        <input
          type="date"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
          value={date}
          onChange={(e) => {
            onChange(joinDateTimeLocal(e.target.value, time, fallbackTime));
            e.currentTarget.blur();
          }}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">{timeLabel}</label>
        <input
          type="time"
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
          value={time}
          disabled={!date}
          onChange={(e) => {
            onChange(joinDateTimeLocal(date, e.target.value, fallbackTime));
          }}
        />
      </div>
    </div>
  );
}

export default function BaristaDashboard() {
  const navigate = useNavigate();
  const ORDER_PAGE_SIZE = 50;
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersRefreshing, setOrdersRefreshing] = useState(false);
  const [loadingOlderOrders, setLoadingOlderOrders] = useState(false);
  const [ordersOffset, setOrdersOffset] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [ordersDateScope, setOrdersDateScope] = useState<'today' | 'all'>('today');
  const hasLoadedOrdersRef = useRef(false);
  const { showToast } = useToast();
  const [staffProfile, setStaffProfile] = useState<{
    cafe_id: string;
    name?: string;
    email?: string;
    is_superuser?: boolean;
  } | null>(null);
  const [selectedCafe, setSelectedCafe] = useState<string>('');
  const [cafes, setCafes] = useState<any[]>([]);
  const [updating, setUpdating] = useState<string | null>(null);
  const [cancelOrderInfo, setCancelOrderInfo] = useState<{id: string, status: string} | null>(null);
  const [activeTab, setActiveTab] = useState<'orders' | 'menu' | 'marketing'>('orders');
  const [activeNav, setActiveNav] = useState<'home' | 'stores' | 'orders' | 'issues' | 'performance' | 'customers' | 'reports' | 'marketing' | 'menu' | 'payments' | 'users' | 'settings'>('home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [performanceSection, setPerformanceSection] = useState<PerformanceSectionId>('sales-volume-ticket');
  const [performanceMenuExpanded, setPerformanceMenuExpanded] = useState(true);
  const [customerSection, setCustomerSection] = useState<CustomerSectionId>('retention');
  const [customerMenuExpanded, setCustomerMenuExpanded] = useState(true);

  useEffect(() => {
    const allowed = new Set(CUSTOMER_NAV_ITEMS.map((i) => i.id));
    if (!allowed.has(customerSection)) {
      setCustomerSection('retention');
    }
  }, [customerSection]);
  const [marketingSection, setMarketingSection] = useState<MarketingSectionId>('campaigns-offers');
  const [marketingMenuExpanded, setMarketingMenuExpanded] = useState(true);
  const [promotionListFilter, setPromotionListFilter] = useState<PromotionListFilter>('all');
  const [requestedOrderHistoryFilter, setRequestedOrderHistoryFilter] = useState<HistoryFilter | null>(null);
  const requestedOrderHistoryFilterRef = useRef<HistoryFilter | null>(null);
  const [homeSelectedOrderId, setHomeSelectedOrderId] = useState<string | null>(null);
  const [pinnedHomeOrder, setPinnedHomeOrder] = useState<Order | null>(null);
  const pinnedHomeOrderRef = useRef<Order | null>(null);
  const activeNavRef = useRef(activeNav);

  const pinHomeOrder = (order: Order | null) => {
    pinnedHomeOrderRef.current = order;
    setPinnedHomeOrder(order);
    if (order) setHomeSelectedOrderId(order.id);
  };

  useEffect(() => {
    activeNavRef.current = activeNav;
  }, [activeNav]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileNavOpen]);

  const currentOrdersListStatus = (): HistoryFilter | null => {
    if (activeNavRef.current === 'home') return 'active';
    const filter = requestedOrderHistoryFilterRef.current;
    if (!filter || filter === 'all') return null;
    return filter;
  };

  const todayOrderBoundsIso = () => {
    const { start, end } = getRangeBounds('today', '', '');
    return { from: start.toISOString(), to: end.toISOString() };
  };

  useEffect(() => {
    if (!MARKETING_NAV_ITEMS.some((i) => i.id === marketingSection)) {
      setMarketingSection('campaigns-offers');
    }
  }, [marketingSection]);
  const [paymentSection, setPaymentSection] = useState<PaymentSectionId>('payments-tab');
  const [paymentMenuExpanded, setPaymentMenuExpanded] = useState(true);
  const [settingsSection, setSettingsSection] = useState<SettingsSectionId>('store-hours');
  const [settingsMenuExpanded, setSettingsMenuExpanded] = useState(true);
  const [storeHoursConfig, setStoreHoursConfig] = useState<StoreHoursConfig | null>(null);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [newMenuItem, setNewMenuItem] = useState({
    name: '',
    description: '',
    category: 'Coffee',
    price: 1.5,
    image_url: '',
    customization_config: suggestCustomizationConfig({
      category: 'Coffee',
      name: '',
    }),
  });
  const menuPlatformFeePct = DEFAULT_MENU_PLATFORM_FEE_PCT;
  const [editingMenuItem, setEditingMenuItem] = useState<{
    id: string;
    name: string;
    description: string;
    category: string;
    price: number;
    image_url: string;
    customization_config: MenuItemCustomizationConfig;
  } | null>(null);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [newPromo, setNewPromo] = useState<PromoFormState>(emptyPromoForm('campaign'));
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [marketingBudgetTier, setMarketingBudgetTier] = useState<
    'starter' | 'growth' | 'pro' | 'uncapped'
  >('growth');
  const [campaignUsage, setCampaignUsage] = useState<{
    active_campaign_count: number;
    campaign_limit: number | null;
    can_create_campaign: boolean;
    homepage_slot_limit: number;
    homepage_per_cafe_limit: number;
    homepage_active_count: number;
    homepage_cafe_active_count: number;
    can_create_homepage_campaign: boolean;
  } | null>(null);
  /** Server-side status totals (works with large history without loading every row). */
  const [orderPipelineCounts, setOrderPipelineCounts] = useState<Record<string, number> | null>(null);
  const [ordersScopeTotal, setOrdersScopeTotal] = useState<number | null>(null);
  const pipelineCountsLoadedRef = useRef(false);
  const fastReadRequest = { timeout: 15000, __skipWakeRetry: true } as any;
  const firstLoadRequest = { timeout: 45000 } as any;
  const lastNetworkToastAtRef = useRef(0);
  const ordersAutoRefreshRef = useRef<() => void>(() => {});
  const ordersRefreshInFlightRef = useRef(false);
  const seenOrderIdsRef = useRef<Set<string>>(new Set());
  const [apiReady, setApiReady] = useState(false);
  const apiWarmupPromiseRef = useRef<Promise<boolean> | null>(null);

  const showDashboardNetworkError = (
    error: any,
    fallback: string,
    opts?: { silentForTimeout?: boolean }
  ) => {
    const isTimeout =
      error?.code === 'ECONNABORTED' ||
      String(error?.message || '').toLowerCase().includes('timeout');
    if (isTimeout && opts?.silentForTimeout) return;
    const message = isTimeout
      ? 'Dashboard data is timing out. Please refresh in a few seconds.'
      : error?.response?.data?.error || error?.message || fallback;
    const now = Date.now();
    if (now - lastNetworkToastAtRef.current < 6000) return;
    lastNetworkToastAtRef.current = now;
    showToast(message, 'error');
  };

  const ensureApiReady = useCallback(async (): Promise<boolean> => {
    if (apiReady) return true;
    if (apiWarmupPromiseRef.current) return apiWarmupPromiseRef.current;
    apiWarmupPromiseRef.current = (async () => {
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          const { data } = await axios.get('/test-server', {
            timeout: 20000,
            __skipWakeRetry: true,
          } as any);
          const status = String(data?.status || '').toLowerCase();
          if (status === 'ok') {
            setApiReady(true);
            return true;
          }
        } catch {
          // continue retry loop
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      return false;
    })();
    try {
      return await apiWarmupPromiseRef.current;
    } finally {
      apiWarmupPromiseRef.current = null;
    }
  }, [apiReady]);

  const MARKETING_BUDGET_PLANS: Record<
    'starter' | 'growth' | 'pro' | 'uncapped',
    {
      label: string;
      monthlyBudget: number | null;
      campaignDaily: number;
      campaignDailyHomepage: number;
      campaignLimit: number | null;
      offerBasicFee: { min: number; max: number };
      offerHighlightedFee: { min: number; max: number };
      defaultPerformanceFee: number;
    }
  > = {
    // Jordan-tuned baseline (lower CPM market; daily values leave room for creative/testing spend)
    starter: {
      label: 'Starter',
      monthlyBudget: 20,
      campaignDaily: 1.0,
      campaignDailyHomepage: 2.5,
      campaignLimit: 2,
      offerBasicFee: { min: 5, max: 10 },
      offerHighlightedFee: { min: 15, max: 20 },
      defaultPerformanceFee: 0.03,
    },
    growth: {
      label: 'Growth',
      monthlyBudget: 50,
      campaignDaily: 2.2,
      campaignDailyHomepage: 4.5,
      campaignLimit: 6,
      offerBasicFee: { min: 5, max: 10 },
      offerHighlightedFee: { min: 15, max: 20 },
      defaultPerformanceFee: 0.04,
    },
    pro: {
      label: 'Pro',
      monthlyBudget: 100,
      campaignDaily: 4.0,
      campaignDailyHomepage: 10.0,
      campaignLimit: 15,
      offerBasicFee: { min: 5, max: 10 },
      offerHighlightedFee: { min: 15, max: 20 },
      defaultPerformanceFee: 0.05,
    },
    uncapped: {
      label: 'Uncapped',
      monthlyBudget: null,
      campaignDaily: 4.0,
      campaignDailyHomepage: 10.0,
      campaignLimit: null,
      offerBasicFee: { min: 5, max: 10 },
      offerHighlightedFee: { min: 15, max: 20 },
      defaultPerformanceFee: 0.05,
    },
  };
  /** Keep in sync with OFFER_BILLING_DAYS_PER_MONTH in server/marketing-plans.js */
  const OFFER_BILLING_DAYS_PER_MONTH = 30;
  const OFFER_TIER_OPTIONS = {
    basic: {
      label: 'Basic offer (standard visibility)',
      hint: 'Listed with other offers — normal placement in cafe and browse lists.',
    },
    highlighted: {
      label: 'Highlighted offer (homepage + featured placement)',
      hint: 'Featured on the home feed and promoted spots — more reach, higher setup fee.',
    },
  } as const;
  const CAMPAIGN_AUDIENCE_OPTIONS = {
    all: { label: 'All customers', hint: 'Everyone browsing QahwaHub can see this boost.' },
    new: { label: 'New customers', hint: 'People who have not ordered from this café yet.' },
    returning: { label: 'Returning customers', hint: 'People who already ordered from this café.' },
  } as const;
  const OFFER_APPLIES_OPTIONS = {
    drinks: {
      label: 'Drinks only',
      hint: 'Coffee, tea, juice, and smoothies. Pastries, sweets, and savory food stay full price. Water does not count.',
    },
    food: {
      label: 'Food only',
      hint: 'Sweets, pastries, and savory food. Drinks stay full price. Water does not count.',
    },
    all: {
      label: 'Whole order',
      hint: 'Every item in the cart, including drinks, sweets, and savory food.',
    },
  } as const;
  const CAMPAIGN_PLACEMENT_OPTIONS = {
    feed: {
      label: 'Feed boost',
      hint: 'Higher in the café list. Guest badge: Sponsored. Every café can buy this — the list is long.',
    },
    homepage: {
      label: 'Homepage featured café',
      hint: 'Featured campaigns row on home (8 cafés at a time, 1 per café). Guest badge: Featured café. Costs more because that row is scarce.',
    },
  } as const;

  const selectedMarketingPlan = MARKETING_BUDGET_PLANS[marketingBudgetTier];
  const selectedCampaignDaily =
    newPromo.campaign_placement === 'homepage'
      ? selectedMarketingPlan.campaignDailyHomepage
      : selectedMarketingPlan.campaignDaily;
  const promoDurationDays =
    newPromo.valid_from && newPromo.valid_until
      ? Math.max(
          0,
          Math.ceil(
            (new Date(newPromo.valid_until).getTime() - new Date(newPromo.valid_from).getTime()) /
              (1000 * 60 * 60 * 24)
          )
        )
      : 0;
  const estimatedPromoCost =
    newPromo.promotion_type === 'offer'
      ? promoDurationDays > 0
        ? Math.round(
            (Math.max(0, Number(newPromo.setup_fee_monthly || 0)) /
              OFFER_BILLING_DAYS_PER_MONTH) *
              promoDurationDays *
              100
          ) / 100
        : 0
      : promoDurationDays > 0
      ? Math.round(promoDurationDays * selectedCampaignDaily * 100) / 100
      : 0;

  useEffect(() => {
    if (editingPromoId) return;
    if (newPromo.promotion_type === 'offer') {
      setNewPromo((prev) => ({
        ...prev,
        budget_total_jod:
          promoDurationDays > 0
            ? Math.round(
                (Math.max(0, Number(prev.setup_fee_monthly || 0)) /
                  OFFER_BILLING_DAYS_PER_MONTH) *
                  promoDurationDays *
                  100
              ) / 100
            : 0,
      }));
      return;
    }
    if (promoDurationDays > 0) {
      const suggested = Math.round(promoDurationDays * selectedCampaignDaily * 100) / 100;
      setNewPromo((prev) => ({ ...prev, budget_total_jod: suggested }));
    }
  }, [
    editingPromoId,
    newPromo.promotion_type,
    newPromo.setup_fee_monthly,
    promoDurationDays,
    selectedCampaignDaily,
  ]);

  useEffect(() => {
    fetchCafes();
    try {
      const raw = getBaristaProfileRawSync();
      if (raw) {
        const b = JSON.parse(raw);
        setStaffProfile(b);
        if (b?.cafe_id && !b?.is_superuser) {
          setSelectedCafe(b.cafe_id);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (activeNav !== 'performance') {
      setPerformanceMenuExpanded(false);
    }
    if (activeNav !== 'customers') {
      setCustomerMenuExpanded(false);
    }
    if (activeNav !== 'marketing') {
      setMarketingMenuExpanded(false);
    }
    if (activeNav !== 'payments') {
      setPaymentMenuExpanded(false);
    }
    if (activeNav !== 'settings') {
      setSettingsMenuExpanded(false);
    }
  }, [activeNav]);

  useEffect(() => {
    // Keep dashboard usable even if cached barista profile is missing/stale.
    const hasBaristaToken =
      typeof window !== 'undefined' && Boolean(getBaristaTokenSync());
    if (!staffProfile && !hasBaristaToken) return;
    if (staffProfile && !staffProfile.is_superuser && !selectedCafe) return;

    setOrdersOffset(0);
    setHasMoreOrders(false);
    seenOrderIdsRef.current = new Set();
    fetchOrders({ append: false, status: currentOrdersListStatus() });
    fetchOrderPipelineCounts();
  }, [selectedCafe, staffProfile]);

  useEffect(() => {
    if (!staffProfile && !(typeof window !== 'undefined' && getBaristaTokenSync())) return;
    setOrdersOffset(0);
    void fetchOrderPipelineCounts();
    if (activeNavRef.current === 'orders') {
      void fetchOrders({ append: false, status: currentOrdersListStatus() });
    }
  }, [ordersDateScope]);

  useEffect(() => {
    if (activeTab === 'menu') {
      fetchMenu();
    }
  }, [activeTab, selectedCafe]);

  useEffect(() => {
    const hasBaristaToken =
      typeof window !== 'undefined' && Boolean(getBaristaTokenSync());
    if (!staffProfile && !hasBaristaToken) return;
    if (staffProfile && !staffProfile.is_superuser && !selectedCafe) return;
    // Marketing APIs are non-critical on Home and can time out under load.
    // Only fetch when user is explicitly in marketing context.
    if (activeTab === 'marketing' || activeNav === 'marketing') {
      fetchPromotions();
      fetchCampaignUsage();
    }
  }, [activeTab, activeNav, selectedCafe, staffProfile]);

  useEffect(() => {
    if (!selectedCafe) {
      setCampaignUsage(null);
      return;
    }
    const cafe = cafes.find((c) => c.id === selectedCafe);
    const tier = cafe?.marketing_budget_tier;
    if (tier === 'starter' || tier === 'growth' || tier === 'pro' || tier === 'uncapped') {
      setMarketingBudgetTier(tier);
    }
  }, [selectedCafe, cafes]);

  const fetchStoreHours = async () => {
    if (!selectedCafe) {
      setStoreHoursConfig(null);
      return;
    }
    try {
      await ensureApiReady();
      const { data } = await axios.get(
        `/api/cafes/${selectedCafe}/store-hours`,
        fastReadRequest
      );
      setStoreHoursConfig(normalizeStoreHours(data));
    } catch {
      setStoreHoursConfig(null);
    }
  };

  useEffect(() => {
    fetchStoreHours();
  }, [selectedCafe]);

  const fetchCafes = async () => {
    try {
      await ensureApiReady();
      const response = await axios.get('/api/cafes', {
        params: { sort: 'name' },
        ...firstLoadRequest,
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setCafes(list);
      if (list.length === 0) {
        showToast('No cafés found — add locations in the database or check the API.', 'error');
      }
    } catch (error: any) {
      console.error('Error fetching cafes:', error);
      showDashboardNetworkError(error, 'Could not load café list');
    }
  };

  const fetchOrders = async (opts?: {
    append?: boolean;
    status?: HistoryFilter | null;
    background?: boolean;
  }) => {
    const append = Boolean(opts?.append);
    const initialLoad = !hasLoadedOrdersRef.current && !append;
    if (!append) {
      if (opts?.background && ordersRefreshInFlightRef.current) return;
      ordersRefreshInFlightRef.current = true;
    }
    try {
      const ready = await ensureApiReady();
      if (!ready) {
        throw new Error('Server is waking up. Please retry in a few seconds.');
      }
      if (append) {
        setLoadingOlderOrders(true);
      } else if (initialLoad) {
        setLoading(true);
      } else {
        setOrdersRefreshing(true);
      }
      const offset = append ? ordersOffset : 0;
      const statusFilter =
        opts && Object.prototype.hasOwnProperty.call(opts, 'status')
          ? opts.status ?? null
          : currentOrdersListStatus();
      const params = new URLSearchParams({
        limit: String(ORDER_PAGE_SIZE),
        offset: String(offset),
        include_meta: '1',
      });
      if (selectedCafe) params.set('cafe_id', selectedCafe);
      if (statusFilter && statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      if (activeNavRef.current !== 'home' && ordersDateScope === 'today') {
        const { from, to } = todayOrderBoundsIso();
        params.set('created_from', from);
        params.set('created_to', to);
      }
      const url = `/api/orders/admin/all?${params.toString()}`;
      console.log('Fetching orders from:', url);
      const response = await axios.get<Order[] | { orders?: Order[]; pagination?: { has_more?: boolean } }>(
        url,
        initialLoad ? firstLoadRequest : fastReadRequest
      );
      console.log('Orders received:', response.data);
      const payload = response.data;
      const incomingOrdersRaw: Order[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.orders)
          ? payload.orders
          : [];
      const incomingOrders = sortNewestFirst(incomingOrdersRaw, (order) => order.created_at);
      alertOnNewPendingOrders(incomingOrders);
      if (append) {
        setOrders((prev) => {
          const seen = new Set(prev.map((o) => o.id));
          const merged = [...prev];
          for (const order of incomingOrders) {
            if (!seen.has(order.id)) merged.push(order);
          }
          return sortNewestFirst(merged, (order) => order.created_at);
        });
        incomingOrders.forEach((order) => seenOrderIdsRef.current.add(order.id));
      } else {
        if (hasLoadedOrdersRef.current) {
          const newcomers = incomingOrders.filter(
            (order) =>
              !seenOrderIdsRef.current.has(order.id) && isActionableOrderStatus(order.status)
          );
          if (newcomers.length > 0) {
            const queued = newcomers.filter(
              (order) => String(order.status || '').toLowerCase() === 'pending'
            );
            const count = queued.length || newcomers.length;
            showToast(
              count === 1 ? 'New order in the queue' : `${count} new orders in the queue`,
              'info'
            );
          }
        }
        incomingOrders.forEach((order) => seenOrderIdsRef.current.add(order.id));
        setOrders(ensurePinnedOrderPresent(incomingOrders || [], pinnedHomeOrderRef.current));
      }
      const fetchedCount = Array.isArray(incomingOrders) ? incomingOrders.length : 0;
      const hasMoreFromApi = Array.isArray(payload)
        ? fetchedCount >= ORDER_PAGE_SIZE
        : Boolean(payload?.pagination?.has_more);
      setHasMoreOrders(hasMoreFromApi);
      setOrdersOffset(offset + fetchedCount);
      if (initialLoad) hasLoadedOrdersRef.current = true;
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      console.error('Error response:', error.response?.data);
      showDashboardNetworkError(error, 'Error loading orders', {
        silentForTimeout: !initialLoad && !append,
      });
    } finally {
      if (append) {
        setLoadingOlderOrders(false);
      } else {
        ordersRefreshInFlightRef.current = false;
        if (initialLoad) {
          setLoading(false);
        } else {
          setOrdersRefreshing(false);
        }
      }
    }
  };

  const fetchOrderPipelineCounts = async () => {
    const initial = !pipelineCountsLoadedRef.current;
    try {
      const ready = await ensureApiReady();
      if (!ready) return;
      const countParams = new URLSearchParams();
      if (selectedCafe) countParams.set('cafe_id', selectedCafe);
      if (ordersDateScope === 'today') {
        const { from, to } = todayOrderBoundsIso();
        countParams.set('created_from', from);
        countParams.set('created_to', to);
      }
      const qs = countParams.toString();
      const url = qs ? `/api/orders/admin/status-counts?${qs}` : '/api/orders/admin/status-counts';
      const { data } = await axios.get(url, initial ? firstLoadRequest : fastReadRequest);
      if (data && typeof data === 'object') {
        const { total_orders: totalOrders, ...counts } = data as Record<string, number> & {
          total_orders?: number;
        };
        const statusKeys = ['pending', 'received', 'preparing', 'ready', 'completed', 'cancelled'] as const;
        const sumFromBuckets = statusKeys.reduce((s, k) => s + (Number((counts as any)[k]) || 0), 0);
        setOrdersScopeTotal(typeof totalOrders === 'number' ? totalOrders : sumFromBuckets);
        setOrderPipelineCounts(counts as Record<string, number>);
        pipelineCountsLoadedRef.current = true;
      } else {
        setOrdersScopeTotal(null);
        setOrderPipelineCounts(null);
      }
    } catch (e: any) {
      console.error('Error fetching order pipeline counts:', e);
      showDashboardNetworkError(e, 'Could not load status counts', {
        silentForTimeout: !initial,
      });
      if (initial) {
        setOrdersScopeTotal(null);
        setOrderPipelineCounts(null);
      }
    }
  };

  useEffect(() => {
    ordersAutoRefreshRef.current = () => {
      const onOrdersSurface = activeNav === 'home' || activeNav === 'orders';
      if (!onOrdersSurface) return;
      if (loadingOlderOrders) return;
      fetchOrders({
        append: false,
        status: currentOrdersListStatus(),
        background: true,
      });
      fetchOrderPipelineCounts();
    };
  }, [activeNav, loadingOlderOrders, selectedCafe, requestedOrderHistoryFilter, ordersDateScope]);

  useLivePoll(
    () => {
      ordersAutoRefreshRef.current();
    },
    3000,
    Boolean(staffProfile || (typeof window !== 'undefined' && getBaristaTokenSync())),
    { hiddenIntervalMs: 20000 }
  );

  const fetchMenu = async () => {
    if (!selectedCafe) {
      setMenuItems([]);
      return;
    }
    try {
      await ensureApiReady();
      setMenuLoading(true);
      const response = await axios.get(`/api/cafes/${selectedCafe}/menu`, fastReadRequest);
      setMenuItems(response.data || []);
    } catch (error: any) {
      console.error('Error fetching menu:', error);
      showDashboardNetworkError(error, 'Failed to load menu items', {
        silentForTimeout: true,
      });
    } finally {
      setMenuLoading(false);
    }
  };

  const fetchPromotions = async () => {
    try {
      await ensureApiReady();
      setPromoLoading(true);
      const url = selectedCafe
        ? `/api/promotions/admin/all?cafe_id=${selectedCafe}`
        : '/api/promotions/admin/all';
      const response = await axios.get<any[]>(url, fastReadRequest);
      setPromotions(sortNewestFirst(response.data || [], (promo) => promo.created_at));
    } catch (error: any) {
      console.error('Error fetching promotions:', error);
      showDashboardNetworkError(error, 'Failed to load promotions', {
        silentForTimeout: true,
      });
    } finally {
      setPromoLoading(false);
    }
  };

  const fetchCampaignUsage = async () => {
    if (!selectedCafe) {
      setCampaignUsage(null);
      return;
    }
    try {
      await ensureApiReady();
      const { data } = await axios.get('/api/promotions/campaign-usage', {
        params: { cafe_id: selectedCafe },
        ...fastReadRequest,
      });
      setCampaignUsage({
        active_campaign_count: Number(data.active_campaign_count || 0),
        campaign_limit: data.campaign_limit == null ? null : Number(data.campaign_limit),
        can_create_campaign: Boolean(data.can_create_campaign),
        homepage_slot_limit: Number(data.homepage_slot_limit || 8),
        homepage_per_cafe_limit: Number(data.homepage_per_cafe_limit || 1),
        homepage_active_count: Number(data.homepage_active_count || 0),
        homepage_cafe_active_count: Number(data.homepage_cafe_active_count || 0),
        can_create_homepage_campaign: Boolean(data.can_create_homepage_campaign),
      });
      if (
        data.marketing_budget_tier === 'starter' ||
        data.marketing_budget_tier === 'growth' ||
        data.marketing_budget_tier === 'pro' ||
        data.marketing_budget_tier === 'uncapped'
      ) {
        setMarketingBudgetTier(data.marketing_budget_tier);
      }
    } catch (error: any) {
      console.error('Error fetching campaign usage:', error);
      setCampaignUsage(null);
    }
  };

  const saveMarketingBudgetTier = async (tier: 'starter' | 'growth' | 'pro' | 'uncapped') => {
    setMarketingBudgetTier(tier);
    if (!selectedCafe) {
      showToast('Select a café in the sidebar (Location) to save this tier.', 'info');
      return;
    }
    try {
      await axios.patch(`/api/cafes/${selectedCafe}/marketing-tier`, {
        marketing_budget_tier: tier,
      });
      setCafes((prev) =>
        prev.map((c) => (c.id === selectedCafe ? { ...c, marketing_budget_tier: tier } : c))
      );
      await fetchCampaignUsage();
      showToast('Marketing tier saved for this cafe', 'success');
    } catch (error: any) {
      console.error('Error saving marketing tier:', error);
      showToast(error.response?.data?.error || 'Failed to save marketing tier', 'error');
      fetchCampaignUsage();
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    // Require confirmation for cancellation
    if (newStatus === 'cancelled') {
      setCancelOrderInfo({ id: orderId, status: newStatus });
      return;
    }

    try {
      setUpdating(orderId);
      await axios.put(`/api/orders/${orderId}/status`, { status: newStatus });
      const pin = pinnedHomeOrderRef.current;
      if (pin && pin.id === orderId) {
        pinHomeOrder(isActionableOrderStatus(newStatus) ? { ...pin, status: newStatus } : null);
      }
      setOrders((prev) => prev.map((order) => (order.id === orderId ? { ...order, status: newStatus } : order)));
      if (!pinnedHomeOrderRef.current) {
        await fetchOrders();
      }
      await fetchOrderPipelineCounts();
      showToast(`Order status updated to: ${newStatus}`, 'success');
    } catch (error: any) {
      console.error('Error updating order status:', error);
      showToast(error.response?.data?.error || 'Failed to update order status', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const confirmCancelOrder = async () => {
    if (!cancelOrderInfo) return;
    const { id, status } = cancelOrderInfo;
    
    try {
      setUpdating(id);
      await axios.put(`/api/orders/${id}/status`, { status });
      const pin = pinnedHomeOrderRef.current;
      if (pin && pin.id === id) {
        pinHomeOrder(isActionableOrderStatus(status) ? { ...pin, status } : null);
      }
      setOrders((prev) => prev.map((order) => (order.id === id ? { ...order, status } : order)));
      if (!pinnedHomeOrderRef.current) {
        await fetchOrders();
      }
      await fetchOrderPipelineCounts();
      showToast(`Order status updated to: ${status}`, 'success');
    } catch (error: any) {
      console.error('Error updating order status:', error);
      showToast(error.response?.data?.error || 'Failed to update order status', 'error');
    } finally {
      setUpdating(null);
      setCancelOrderInfo(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-[#f6eadb] text-[#8b613f] border-[#dec3a8]';
      case 'received':
        return 'bg-[#f4e5d4] text-[#805437] border-[#d5b495]';
      case 'preparing':
        return 'bg-[#efdfcd] text-[#73492f] border-[#ccaa8a]';
      case 'ready':
        return 'bg-[#ede6dc] text-[#7a4b28] border-[#cfb291]';
      case 'completed':
        return 'bg-[#e8ddd0] text-[#6f5845] border-[#c8ae93]';
      case 'cancelled':
        return 'bg-[#f4dfd8] text-[#9d4b3e] border-[#d7a397]';
      default:
        return 'bg-[#e8ddd0] text-[#6f5845] border-[#c8ae93]';
    }
  };

  const getStatusButtons = (currentStatus: string, orderId: string) => {
    const statusFlow = ['pending', 'received', 'preparing', 'ready', 'completed'];
    const currentIndex = statusFlow.indexOf(currentStatus);

    if (currentStatus === 'cancelled' || currentStatus === 'completed') {
      return null;
    }

    const nextStatus = statusFlow[currentIndex + 1];
    const statusLabels: { [key: string]: string } = {
      'received': 'Mark as Received',
      'preparing': 'Start Preparing',
      'ready': 'Mark as Ready',
      'completed': 'Mark as Completed'
    };

    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {nextStatus && (
          <button
            type="button"
            onClick={() => updateOrderStatus(orderId, nextStatus)}
            disabled={updating === orderId}
            className={`px-4 py-2 rounded font-semibold text-sm transition ${
              nextStatus === 'received' ? 'bg-[#8f5f3e] hover:bg-[#7c5134] text-white' :
              nextStatus === 'preparing' ? 'bg-[#7c5134] hover:bg-[#69442b] text-white' :
              nextStatus === 'ready' ? 'bg-[#6d472e] hover:bg-[#5c3b26] text-white' :
              'bg-[#5f4c3d] hover:bg-[#4f3f32] text-white'
            } disabled:opacity-50`}
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: updating === orderId ? 'none' : 'auto' }}
          >
            {updating === orderId ? 'Updating...' : statusLabels[nextStatus]}
          </button>
        )}
        {currentStatus !== 'cancelled' && (
          <button
            type="button"
            onClick={() => updateOrderStatus(orderId, 'cancelled')}
            disabled={updating === orderId}
            className="px-4 py-2 rounded font-semibold text-sm bg-[#b46854] hover:bg-[#a25947] text-white disabled:opacity-50"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: updating === orderId ? 'none' : 'auto' }}
          >
            Cancel Order
          </button>
        )}
      </div>
    );
  };

  const activeOrders = useMemo(
    () => mergePinnedIntoActiveOrders(orders, pinnedHomeOrder),
    [orders, pinnedHomeOrder]
  );
  const selectedHomeOrder =
    activeOrders.find((o) => o.id === homeSelectedOrderId) ||
    (pinnedHomeOrder && isActionableOrderStatus(pinnedHomeOrder.status) ? pinnedHomeOrder : null) ||
    activeOrders[0] ||
    null;
  useEffect(() => {
    if (!activeOrders.length) {
      if (homeSelectedOrderId !== null) setHomeSelectedOrderId(null);
      return;
    }
    if (homeSelectedOrderId && activeOrders.some((o) => o.id === homeSelectedOrderId)) {
      return;
    }
    if (pinnedHomeOrder && activeOrders.some((o) => o.id === pinnedHomeOrder.id)) {
      setHomeSelectedOrderId(pinnedHomeOrder.id);
      return;
    }
    setHomeSelectedOrderId(activeOrders[0].id);
  }, [activeOrders, homeSelectedOrderId, pinnedHomeOrder]);

  if (loading && orders.length === 0 && cafes.length === 0) {
    return (
      <div className={qhShell.loadingBox}>
        <div className="rounded-2xl border border-[#d8cbb9] bg-[#fffcf7] px-10 py-8 shadow-lg">
          <p
            className="text-lg text-[#4a3d35]"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            Brewing your orders…
          </p>
          <p className="mt-2 text-sm text-[#7a6a5c]">QahwaHub Manager</p>
        </div>
      </div>
    );
  }

  // Group orders by status (normalized; used when pipeline API counts unavailable)
  const ordersByStatus = {
    pending: orders.filter((o) => (o.status || '').toLowerCase().trim() === 'pending'),
    received: orders.filter((o) => (o.status || '').toLowerCase().trim() === 'received'),
    preparing: orders.filter((o) => (o.status || '').toLowerCase().trim() === 'preparing'),
    ready: orders.filter((o) => (o.status || '').toLowerCase().trim() === 'ready'),
    completed: orders.filter((o) => (o.status || '').toLowerCase().trim() === 'completed'),
    cancelled: orders.filter((o) => (o.status || '').toLowerCase().trim() === 'cancelled'),
  };

  const pipelineCount = (key: keyof typeof ordersByStatus) => {
    if (orderPipelineCounts && typeof orderPipelineCounts[key] === 'number') {
      return orderPipelineCounts[key];
    }
    return ordersByStatus[key].length;
  };

  // Calculate today's summary metrics
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayOrders = orders.filter(o => {
    const orderDate = new Date(o.created_at);
    orderDate.setHours(0, 0, 0, 0);
    return orderDate.getTime() === today.getTime();
  });
  const todaySales = todayOrders.reduce((sum, o) => sum + o.total_amount, 0);
  const todayOrderCount = todayOrders.length;
  const avgTicketSize = todayOrderCount > 0 ? todaySales / todayOrderCount : 0;

  const isSupportAdmin = Boolean(staffProfile?.is_superuser);

  const navItems: {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    hasDropdown?: boolean;
    group: string;
  }[] = [
    { id: 'home', label: 'Home', icon: FaHome, group: 'Your shift' },
    { id: 'stores', label: 'Stores', icon: FaStore, group: 'Your shift' },
    { id: 'orders', label: 'Orders', icon: FaClipboardList, group: 'Your shift' },
    { id: 'issues', label: 'Issue reports', icon: FaExclamationTriangle, group: 'Your shift' },
    { id: 'performance', label: 'Performance', icon: FaChartBar, hasDropdown: true, group: 'Pulse & guests' },
    { id: 'customers', label: 'Customers', icon: FaUsers, hasDropdown: true, group: 'Pulse & guests' },
    { id: 'reports', label: 'Reports', icon: FaFileAlt, group: 'Signals & buzz' },
    { id: 'marketing', label: 'Marketing', icon: FaBullhorn, hasDropdown: true, group: 'Signals & buzz' },
    { id: 'menu', label: 'Menu', icon: FaUtensils, group: 'What you serve' },
    { id: 'payments', label: 'Payments', icon: FaWallet, hasDropdown: true, group: 'Treasury' },
    { id: 'users', label: 'Users', icon: FaUserFriends, hasDropdown: false, group: 'Crew & control' },
    { id: 'settings', label: 'Settings', icon: FaCog, hasDropdown: true, group: 'Crew & control' },
  ].filter((item) => item.id !== 'issues' || isSupportAdmin);

  // Map navigation items to tabs/content
  const handleNavClick = (navId: string) => {
    setActiveNav(navId as any);
    if (navId === 'orders') {
      setActiveTab('orders');
      void fetchOrders({
        append: false,
        status: requestedOrderHistoryFilterRef.current,
      });
    }
    if (navId === 'issues') setActiveTab('orders');
    if (navId === 'menu') setActiveTab('menu');
    if (navId === 'marketing') {
      setActiveTab('marketing');
      setMarketingMenuExpanded(true);
    }
    if (navId === 'home') {
      requestedOrderHistoryFilterRef.current = null;
      setRequestedOrderHistoryFilter(null);
      const focused = pinnedHomeOrderRef.current;
      if (!focused || !isActionableOrderStatus(focused.status)) {
        void fetchOrders({ append: false, status: 'active' });
      }
    }
    setMobileNavOpen(false);
  };

  const jumpToMarketingAnchor = (elementId: string) => {
    scrollToPageTarget(elementId);
  };

  const jumpToMarketingReports = () => {
    setActiveNav('reports');
    window.setTimeout(() => scrollToPageTarget('reports-export-marketing-spend'), 150);
  };

  const jumpToMultiStorePromotions = () => {
    const locationSelect = document.getElementById('qh-cafe-select');
    locationSelect?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    locationSelect?.focus();
    jumpToMarketingAnchor('mkt-promotions-list');
  };

  const jumpToBudgetPlanner = () => {
    setNewPromo((prev) => ({ ...prev, promotion_type: 'campaign' }));
    window.setTimeout(() => scrollToPageTarget('mkt-budget-planner'), 50);
  };

  const openMarketingPromotions = (filter: PromotionListFilter) => {
    handleNavClick('marketing');
    setMarketingSection('campaign-management');
    setPromotionListFilter(filter);
    window.setTimeout(() => scrollToPageTarget('mkt-promotions-list'), 120);
  };

  const openOrdersByFilter = (filter: HistoryFilter) => {
    requestedOrderHistoryFilterRef.current = filter;
    setRequestedOrderHistoryFilter(filter);
    setOrdersOffset(0);
    handleNavClick('orders');
    void fetchOrders({ append: false, status: filter });
    window.setTimeout(() => scrollToPageTarget('orders-history-panel'), 120);
  };

  const mainTitle =
    activeNav === 'orders'
      ? 'Orders & operations'
      : activeNav === 'issues'
      ? 'Issue reports'
      : activeNav === 'reports'
      ? 'Reports'
      : activeNav === 'performance'
      ? 'Performance'
      : activeNav === 'customers'
      ? 'Customers'
      : activeNav === 'marketing'
      ? 'Marketing'
      : activeNav === 'menu'
      ? 'Menu'
      : activeNav === 'payments'
      ? 'Payments'
      : activeNav === 'users'
      ? 'Users'
      : activeNav === 'settings'
      ? 'Settings'
      : "Today's summary";

  const performanceSectionLabel = PERFORMANCE_NAV_ITEMS.find((x) => x.id === performanceSection)?.label;
  const customerSectionLabel = CUSTOMER_NAV_ITEMS.find((x) => x.id === customerSection)?.label;
  const marketingSectionLabel = MARKETING_NAV_ITEMS.find((x) => x.id === marketingSection)?.label;
  const paymentSectionLabel = PAYMENTS_NAV_ITEMS.find((x) => x.id === paymentSection)?.label;
  const settingsSectionLabel = SETTINGS_NAV_ITEMS.find((x) => x.id === settingsSection)?.label;
  const selectedCafeName = cafes.find((c) => c.id === selectedCafe)?.name;
  const campaignPromotions = promotions.filter((p) => (p.promotion_type || 'campaign') === 'campaign');
  const offerPromotions = promotions.filter((p) => p.promotion_type === 'offer');
  const filteredPromotions = sortNewestFirst(
    promotions.filter((promo) => {
      const type = (promo.promotion_type || 'campaign') === 'offer' ? 'offer' : 'campaign';
      if (promotionListFilter === 'all') return true;
      if (promotionListFilter === 'active') return Boolean(promo.active);
      return type === promotionListFilter;
    }),
    (promo) => promo.created_at
  );
  const mainSubtitle =
    activeNav === 'orders'
      ? 'History, transaction details, timings, status health, and report exports.'
      : activeNav === 'issues'
      ? 'Review customer reports, update status, and notify customers by email.'
      : activeNav === 'reports'
      ? 'Operational and financial Excel exports.'
      : activeNav === 'performance'
      ? `Business health · ${performanceSectionLabel ?? ''}`
      : activeNav === 'customers'
      ? `Insights · ${customerSectionLabel ?? ''}`
      : activeNav === 'marketing'
      ? `Grow on QahwaHub · ${marketingSectionLabel ?? 'Create & manage campaigns'}`
      : activeNav === 'menu'
      ? 'Items, options, and availability for this café.'
      : activeNav === 'payments'
      ? `Payments guide · ${paymentSectionLabel ?? ''}`
      : activeNav === 'users'
      ? 'Team & access management'
      : activeNav === 'settings'
      ? `Café setup · ${settingsSectionLabel ?? ''}`
      : undefined;

  const baristaSurfaceTheme =
    "[&_.bg-white]:bg-[#fffaf4] [&_.bg-gray-50]:bg-[#f5e9dc] [&_.border-gray-100]:border-[#eadfce] [&_.border-gray-200]:border-[#e2d4c1] [&_.text-gray-900]:text-[#4a2e1f] [&_.text-gray-800]:text-[#5a3723] [&_.text-gray-700]:text-[#6a4a33] [&_.text-gray-600]:text-[#7d6a58] [&_.text-gray-500]:text-[#8d7a68] dark:[&_.bg-white]:bg-[#1f1814] dark:[&_.bg-gray-50]:bg-[#261d17] dark:[&_.border-gray-100]:border-[#3b2f28] dark:[&_.border-gray-200]:border-[#4a3a30] dark:[&_.text-gray-900]:text-[#f2e6d8] dark:[&_.text-gray-800]:text-[#e8dacb] dark:[&_.text-gray-700]:text-[#d9c7b5] dark:[&_.text-gray-600]:text-[#c9b7a7] dark:[&_.text-gray-500]:text-[#b6a395]";

  const mobileCafeLabel =
    cafes.find((cafe) => cafe.id === selectedCafe)?.name || 'All cafés';

  return (
    <div
      className={qhShell.page}
      style={{ fontFamily: "'DM Sans', ui-sans-serif, system-ui, sans-serif" }}
    >
      {mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-[#1a1410]/45 lg:hidden"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <aside
        className={`${qhShell.sidebar} ${mobileNavOpen ? qhShell.sidebarOpen : qhShell.sidebarClosed}`}
      >
        <div className="border-b border-[#e2d4c2] px-4 pb-5 pt-5 dark:border-[#2f2823]">
          <div className="flex items-start gap-3">
            <div className={qhShell.logoMark} aria-hidden>
              ☕
            </div>
            <div className={qhShell.brandBlock}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className={qhShell.brandTitle}>QahwaHub</h2>
                  <p className={qhShell.brandTagline}>Roastery & café console</p>
                </div>
                <button
                  type="button"
                  className={`${qhShell.menuBtn} lg:hidden`}
                  aria-label="Close menu"
                  onClick={() => setMobileNavOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <label className={qhShell.cafeLabel} htmlFor="qh-cafe-select">
                Location
              </label>
              <select
                id="qh-cafe-select"
                value={selectedCafe}
                onChange={(e) => setSelectedCafe(e.target.value)}
                className={qhShell.cafeSelect}
                disabled={!!staffProfile?.cafe_id && !staffProfile?.is_superuser}
                title={
                  staffProfile?.cafe_id && !staffProfile?.is_superuser
                    ? 'Locked to your assigned cafe'
                    : undefined
                }
              >
                {(!staffProfile?.cafe_id || staffProfile?.is_superuser) && (
                  <option value="" className="bg-[#fff9f1] text-[#4a2e1f] dark:bg-[#231e1a] dark:text-[#f0e8df]">
                    All cafés
                  </option>
                )}
                {cafes.map((cafe) => (
                  <option
                    key={cafe.id}
                    value={cafe.id}
                    className="bg-[#fff9f1] text-[#4a2e1f] dark:bg-[#231e1a] dark:text-[#f0e8df]"
                  >
                    {cafe.name}
                  </option>
                ))}
              </select>
              {cafes.length === 0 && (
                <p className="mt-1.5 text-[10px] text-[#8a7058] dark:text-[#c4a882]">
                  Café list empty — refresh after the API is running.
                </p>
              )}
              {staffProfile && (
                <button
                  type="button"
                  onClick={() => {
                    void setBaristaToken(null);
                    void setBaristaProfile(null);
                    delete axios.defaults.headers.common['Authorization'];
                    navigate('/barista/login', { replace: true });
                  }}
                  className={qhShell.signOut}
                >
                  Staff sign out
                </button>
              )}
              <p
                className="mt-2 text-[10px] text-[#8a7058] tabular-nums dark:text-[#6b5f54]"
                title={`If this is not v${packageJson.version}, your browser or host is serving an old build.`}
              >
                Console v{packageJson.version}
              </p>
            </div>
          </div>
        </div>
        <nav className={qhShell.navScroll} aria-label="Primary">
          {navItems.map((item, navIndex) => {
            const showGroup =
              navIndex === 0 || navItems[navIndex - 1].group !== item.group;
            const groupHeader = showGroup ? (
              <p key={`gh-${item.group}-${navIndex}`} className={qhShell.navGroupLabel}>
                {item.group}
              </p>
            ) : null;

            if (item.id === 'performance') {
              const Icon = item.icon;
              const perfActive = activeNav === 'performance';
              return (
                <Fragment key="performance">
                  {groupHeader}
                  <div>
                    <button
                      type="button"
                      aria-expanded={perfActive && performanceMenuExpanded}
                      onClick={() => {
                        if (!perfActive) {
                          setActiveNav('performance');
                          setPerformanceMenuExpanded(true);
                        } else {
                          setPerformanceMenuExpanded((open) => !open);
                        }
                      }}
                      className={qhShell.navRow(perfActive)}
                    >
                      <QhNavIcon active={perfActive}>
                        <Icon className="text-[15px]" />
                      </QhNavIcon>
                      <span className="flex-1 text-left">{item.label}</span>
                      <span
                        className={qhShell.chevron(perfActive && performanceMenuExpanded)}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {perfActive && performanceMenuExpanded && (
                      <div className={qhShell.subNavWrap}>
                        {PERFORMANCE_NAV_ITEMS.map((sub, idx) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => {
                              setActiveNav('performance');
                              setPerformanceSection(sub.id);
                              setPerformanceMenuExpanded(true);
                              setMobileNavOpen(false);
                            }}
                            className={qhShell.subNavItem(performanceSection === sub.id)}
                          >
                            <span className={qhShell.subIndex}>{idx + 1}.</span>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            }

            if (item.id === 'customers') {
              const Icon = item.icon;
              const custActive = activeNav === 'customers';
              return (
                <Fragment key="customers">
                  {groupHeader}
                  <div>
                    <button
                      type="button"
                      aria-expanded={custActive && customerMenuExpanded}
                      onClick={() => {
                        if (!custActive) {
                          setActiveNav('customers');
                          setCustomerMenuExpanded(true);
                        } else {
                          setCustomerMenuExpanded((open) => !open);
                        }
                      }}
                      className={qhShell.navRow(custActive)}
                    >
                      <QhNavIcon active={custActive}>
                        <Icon className="text-[15px]" />
                      </QhNavIcon>
                      <span className="flex-1 text-left">{item.label}</span>
                      <span
                        className={qhShell.chevron(custActive && customerMenuExpanded)}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {custActive && customerMenuExpanded && (
                      <div className={qhShell.subNavWrap}>
                        {CUSTOMER_NAV_ITEMS.map((sub, idx) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => {
                              setActiveNav('customers');
                              setCustomerSection(sub.id);
                              setCustomerMenuExpanded(true);
                              setMobileNavOpen(false);
                            }}
                            className={qhShell.subNavItem(customerSection === sub.id)}
                          >
                            <span className={qhShell.subIndex}>{idx + 1}.</span>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            }

            if (item.id === 'marketing') {
              const Icon = item.icon;
              const mktActive = activeNav === 'marketing';
              return (
                <Fragment key="marketing">
                  {groupHeader}
                  <div>
                    <button
                      type="button"
                      aria-expanded={mktActive && marketingMenuExpanded}
                      onClick={() => {
                        if (!mktActive) {
                          setActiveNav('marketing');
                          setActiveTab('marketing');
                          setMarketingMenuExpanded(true);
                        } else {
                          setMarketingMenuExpanded((open) => !open);
                        }
                      }}
                      className={qhShell.navRow(mktActive)}
                    >
                      <QhNavIcon active={mktActive}>
                        <Icon className="text-[15px]" />
                      </QhNavIcon>
                      <span className="flex-1 text-left">{item.label}</span>
                      <span
                        className={qhShell.chevron(mktActive && marketingMenuExpanded)}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {mktActive && marketingMenuExpanded && (
                      <div className={qhShell.subNavWrap}>
                        {MARKETING_NAV_ITEMS.map((sub, idx) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => {
                              setActiveNav('marketing');
                              setActiveTab('marketing');
                              setMarketingSection(sub.id);
                              setMarketingMenuExpanded(true);
                              setMobileNavOpen(false);
                            }}
                            className={qhShell.subNavItem(marketingSection === sub.id)}
                          >
                            <span className={qhShell.subIndex}>{idx + 1}.</span>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            }

            if (item.id === 'settings') {
              const Icon = item.icon;
              const setActive = activeNav === 'settings';
              return (
                <Fragment key="settings">
                  {groupHeader}
                  <div>
                    <button
                      type="button"
                      aria-expanded={setActive && settingsMenuExpanded}
                      onClick={() => {
                        if (!setActive) {
                          setActiveNav('settings');
                          setSettingsMenuExpanded(true);
                        } else {
                          setSettingsMenuExpanded((open) => !open);
                        }
                      }}
                      className={qhShell.navRow(setActive)}
                    >
                      <QhNavIcon active={setActive}>
                        <Icon className="text-[15px]" />
                      </QhNavIcon>
                      <span className="flex-1 text-left">{item.label}</span>
                      <span
                        className={qhShell.chevron(setActive && settingsMenuExpanded)}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {setActive && settingsMenuExpanded && (
                      <div className={qhShell.subNavWrap}>
                        {SETTINGS_NAV_ITEMS.map((sub, idx) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => {
                              setActiveNav('settings');
                              setSettingsSection(sub.id);
                              setSettingsMenuExpanded(true);
                              setMobileNavOpen(false);
                            }}
                            className={qhShell.subNavItem(settingsSection === sub.id)}
                          >
                            <span className={qhShell.subIndex}>{idx + 1}.</span>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            }

            if (item.id === 'payments') {
              const Icon = item.icon;
              const payActive = activeNav === 'payments';
              return (
                <Fragment key="payments">
                  {groupHeader}
                  <div>
                    <button
                      type="button"
                      aria-expanded={payActive && paymentMenuExpanded}
                      onClick={() => {
                        if (!payActive) {
                          setActiveNav('payments');
                          setPaymentMenuExpanded(true);
                        } else {
                          setPaymentMenuExpanded((open) => !open);
                        }
                      }}
                      className={qhShell.navRow(payActive)}
                    >
                      <QhNavIcon active={payActive}>
                        <Icon className="text-[15px]" />
                      </QhNavIcon>
                      <span className="flex-1 text-left">{item.label}</span>
                      <span
                        className={qhShell.chevron(payActive && paymentMenuExpanded)}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {payActive && paymentMenuExpanded && (
                      <div className={qhShell.subNavWrap}>
                        {PAYMENTS_NAV_ITEMS.map((sub, idx) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => {
                              setActiveNav('payments');
                              setPaymentSection(sub.id);
                              setPaymentMenuExpanded(true);
                              setMobileNavOpen(false);
                            }}
                            className={qhShell.subNavItem(paymentSection === sub.id)}
                          >
                            <span className={qhShell.subIndex}>{idx + 1}.</span>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            }

            const Icon = item.icon;
            const isActive = activeNav === item.id;
            return (
              <Fragment key={item.id}>
                {groupHeader}
                <button
                  type="button"
                  onClick={() => handleNavClick(item.id)}
                  className={qhShell.navRow(isActive)}
                >
                  <QhNavIcon active={isActive}>
                    <Icon className="text-[15px]" />
                  </QhNavIcon>
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.hasDropdown &&
                    item.id !== 'customers' &&
                    item.id !== 'performance' &&
                    item.id !== 'marketing' &&
                    item.id !== 'payments' &&
                    item.id !== 'settings' &&
                    item.id !== 'users' && (
                      <span className="text-[10px] text-[#8a7058] dark:text-[#6e635a]">▼</span>
                    )}
                </button>
              </Fragment>
            );
          })}
        </nav>
        <div className="mt-auto shrink-0 border-t border-[#e2d4c2] px-3 pb-5 pt-4 dark:border-[#2f2823]">
          <p className="px-1 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a7058] dark:text-[#5c534c]">
            Different product
          </p>
          <Link
            to="/cafes"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#d8c5b0] bg-[#fff9f1] px-3 py-2.5 text-center text-xs font-semibold text-[#7b4b28] transition hover:border-[#c45c26]/55 hover:text-[#4a2e1f] dark:border-[#3d3530] dark:bg-[#231e1a] dark:text-[#cfc4b8] dark:hover:text-[#fff5eb]"
          >
            QahwaHub customer app →
          </Link>
        </div>
      </aside>

      <div className={qhShell.mobileBar}>
        <button
          type="button"
          className={qhShell.menuBtn}
          aria-label="Open menu"
          onClick={() => setMobileNavOpen(true)}
        >
          <FaBars />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-[#4a2e1f] dark:text-[#f5eadf]">QahwaHub</p>
          <p className="truncate text-xs text-[#8a7058] dark:text-[#9d8c7f]">{mobileCafeLabel}</p>
        </div>
      </div>

      <main className={`${qhShell.main} ${baristaSurfaceTheme}`}>
        <div className={qhShell.mainInner}>
          <header className={qhShell.headerCard}>
            <div>
              <p className={qhShell.greeting}>
                Good{' '}
                {new Date().getHours() < 12
                  ? 'morning'
                  : new Date().getHours() < 18
                  ? 'afternoon'
                  : 'evening'}
                <span className="text-[#b8956a]"> · </span>
                <span className="italic text-[#9a8575]">Saha</span>
              </p>
              <h1 className={qhShell.pageTitle}>{mainTitle}</h1>
              {mainSubtitle && <p className={qhShell.pageSubtitle}>{mainSubtitle}</p>}
            </div>
            <button
              type="button"
              onClick={() => {
                const focused = pinnedHomeOrderRef.current;
                if (!focused || !isActionableOrderStatus(focused.status)) {
                  fetchOrders({ append: false });
                }
                fetchOrderPipelineCounts();
                if (activeTab === 'menu') fetchMenu();
                if (activeTab === 'marketing') fetchPromotions();
              }}
              className={qhShell.refreshBtn}
            >
              {ordersRefreshing ? 'Refreshing…' : 'Refresh board'}
            </button>
          </header>

          {/* Spotlight & quick pulls */}
          {activeNav === 'home' && (
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="relative overflow-hidden rounded-2xl border border-[#c9b8a4] bg-gradient-to-br from-[#3d2918] via-[#2a1d14] to-[#1a1410] p-7 text-[#f5ebe0] shadow-xl lg:col-span-2">
              <div
                className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-[#c45c26]/25 blur-3xl"
                aria-hidden
              />
              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#c45c26]/30 text-4xl ring-1 ring-white/10">
                  ✦
                </div>
                <div className="w-full">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#c4a882]">
                    QahwaHub marketing
                  </p>
                  <h3
                    className="mt-1 text-xl font-semibold leading-snug sm:text-2xl"
                    style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                  >
                    Marketing control center—launch faster, track spend, grow regulars.
                  </h3>
                  <p className="mt-2 text-sm text-[#c9b8a8]">
                    Turn this into action: pick a cafe, launch a campaign or offer, and keep spend in range.
                  </p>
                  <p className="mt-1 text-xs text-[#a89888]">
                    Figures below are <span className="font-semibold text-[#d8c5b3]">saved promotions</span> — not
                    order volume. Order pipeline counts are under &ldquo;Queued–Cancelled&rdquo;.
                  </p>
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <button
                      type="button"
                      onClick={() => openMarketingPromotions('all')}
                      className="rounded-lg bg-white/10 border border-white/10 p-2 hover:bg-white/20 transition text-left"
                    >
                      <p className="text-[10px] uppercase tracking-wide text-[#d8c5b3]">Total</p>
                      <p className="text-lg font-semibold">{promotions.length}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => openMarketingPromotions('active')}
                      className="rounded-lg bg-white/10 border border-white/10 p-2 hover:bg-white/20 transition text-left"
                    >
                      <p className="text-[10px] uppercase tracking-wide text-[#d8c5b3]">Active</p>
                      <p className="text-lg font-semibold">{promotions.filter((p) => p.active).length}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => openMarketingPromotions('campaign')}
                      className="rounded-lg bg-white/10 border border-white/10 p-2 hover:bg-white/20 transition text-left"
                    >
                      <p className="text-[10px] uppercase tracking-wide text-[#d8c5b3]">Campaigns</p>
                      <p className="text-lg font-semibold">{campaignPromotions.length}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => openMarketingPromotions('offer')}
                      className="rounded-lg bg-white/10 border border-white/10 p-2 hover:bg-white/20 transition text-left"
                    >
                      <p className="text-[10px] uppercase tracking-wide text-[#d8c5b3]">Offers</p>
                      <p className="text-lg font-semibold">{offerPromotions.length}</p>
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleNavClick('marketing');
                        setActiveTab('marketing');
                      }}
                      className="px-3 py-2 rounded-lg bg-[#c45c26] text-white text-sm font-semibold hover:bg-[#a94f21] transition"
                    >
                      Create Campaign
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleNavClick('marketing');
                        setActiveTab('marketing');
                        setNewPromo((prev) => ({ ...prev, promotion_type: 'offer' }));
                      }}
                      className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-[#f5ebe0] text-sm font-semibold hover:bg-white/20 transition"
                    >
                      Create Offer
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#d8cbb9] bg-[#fffcf7]/95 p-5 shadow-sm">
              <h3
                className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-[#7a6558]"
                style={{ fontFamily: "'Fraunces', Georgia, serif" }}
              >
                Quick pulls
              </h3>
              <div className="space-y-2">
                {[
                  { nav: 'stores' as const, icon: FaHome, label: 'Store presence', hint: 'Locations' },
                  { nav: 'marketing' as const, icon: FaBullhorn, label: 'Steep a promo', hint: 'Marketing' },
                  { nav: 'menu' as const, icon: FaEdit, label: 'Tweak the board', hint: 'Menu' },
                  { nav: 'settings' as const, icon: FaClock, label: 'Pour hours', hint: 'Settings' },
                ].map((row) => (
                  <button
                    key={row.nav}
                    type="button"
                    onClick={() => handleNavClick(row.nav)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition hover:border-[#e0d0c0] hover:bg-[#faf6f0]"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#ede4d7] text-[#8b5a2b]">
                        <row.icon className="text-sm" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-[#2a2420]">{row.label}</span>
                        <span className="text-xs text-[#8a7a6e]">{row.hint}</span>
                      </span>
                    </span>
                    <span className="text-[#c45c26]">→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}

          {/* Cup line — order pipeline */}
          {(activeNav === 'home' || activeNav === 'orders') && (
          <>
          <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
            {(
              [
                { key: 'pending', label: 'Queued', accent: 'from-[#d4a017] to-[#a67c00]', filter: 'pending' as HistoryFilter },
                { key: 'received', label: 'Pulled', accent: 'from-[#5b8fc7] to-[#3d6ea3]', filter: 'received' as HistoryFilter },
                { key: 'preparing', label: 'Crafting', accent: 'from-[#9b6bb8] to-[#6b4088]', filter: 'preparing' as HistoryFilter },
                { key: 'ready', label: 'On bar', accent: 'from-[#3d9a5c] to-[#27663d]', filter: 'ready' as HistoryFilter },
                { key: 'completed', label: 'Served', accent: 'from-[#7a7268] to-[#4a443d]', filter: 'completed' as HistoryFilter },
                { key: 'cancelled', label: 'Cancelled', accent: 'from-[#c44a4a] to-[#8f3030]', filter: 'cancelled' as HistoryFilter },
              ] as const
            ).map((row) => (
              <button
                key={row.key}
                type="button"
                onClick={() => openOrdersByFilter(row.filter)}
                className="rounded-2xl border border-[#e5d9cb] bg-[#fffcf7] p-4 text-center shadow-sm transition hover:border-amber-300 hover:bg-amber-50/50"
              >
                <div
                  className={`mx-auto mb-2 h-1.5 w-10 rounded-full bg-gradient-to-r ${row.accent}`}
                  aria-hidden
                />
                <div
                  className="text-2xl font-semibold tabular-nums text-[#1e1714]"
                  style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                >
                  {pipelineCount(row.key)}
                </div>
                <div className="text-xs font-medium uppercase tracking-wide text-[#7a6a5c]">
                  {row.label}
                </div>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-[#7a6a5c] max-w-4xl">
            <span className="font-semibold text-[#4a3d35]">Queued–Crafting</span> are in-flight stages only (often
            zero when nothing is live). <span className="font-semibold">Served</span>,{' '}
            <span className="font-semibold">Cancelled</span>, and <span className="font-semibold">On bar</span> include
            completed, cancelled, and ready orders. Counts default to <span className="font-semibold">today</span>
            {' '}(café filter + sign-in). Switch to All dates to include older tickets.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(
              [
                { id: 'today' as const, label: 'Today' },
                { id: 'all' as const, label: 'All dates' },
              ]
            ).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setOrdersDateScope(c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  ordersDateScope === c.id
                    ? 'bg-[#3d2918] text-[#fff8f2]'
                    : 'bg-white border border-[#e5d9cb] text-[#5a4a3e] hover:border-amber-300'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {ordersScopeTotal !== null && (
            <p className="mt-1 text-sm text-[#2a2420]">
              <span className="font-semibold tabular-nums">{ordersScopeTotal}</span> orders in this view (all
              statuses).
            </p>
          )}
          <div className="mt-4">
            <ScanDineIn
              cafeId={selectedCafe}
              onConverted={() => {
                void fetchOrders({ append: false });
              }}
            />
          </div>
          </>
          )}

          {/* Content based on active navigation */}
          {activeNav === 'orders' && (
            <div className="space-y-4">
              <OrderOperationsPanel
                orders={orders}
                getStatusColor={getStatusColor}
                getStatusButtons={getStatusButtons}
                onNavigateToReports={() => handleNavClick('reports')}
                onOpenOrderForAction={(order) => {
                  const actionable = isActionableOrderStatus(order.status);
                  if (actionable) {
                    const pinned = order as Order;
                    requestedOrderHistoryFilterRef.current = null;
                    setRequestedOrderHistoryFilter(null);
                    pinHomeOrder(pinned);
                    setOrders((prev) => ensurePinnedOrderPresent(prev, pinned));
                    setActiveNav('home');
                    scrollToPageTarget('home-active-orders-panel');
                  } else {
                    showToast('This order is already completed/cancelled and cannot be actioned.', 'info');
                  }
                }}
                requestedHistoryFilter={requestedOrderHistoryFilter}
                onClearRequestedHistoryFilter={() => {
                  requestedOrderHistoryFilterRef.current = null;
                  setRequestedOrderHistoryFilter(null);
                  setOrdersOffset(0);
                  void fetchOrders({ append: false, status: null });
                }}
                onSelectHistoryFilter={(filter) => {
                  const next = filter === 'all' ? null : filter;
                  requestedOrderHistoryFilterRef.current = next;
                  setRequestedOrderHistoryFilter(next);
                  setOrdersOffset(0);
                  void fetchOrders({ append: false, status: next });
                }}
                pipelineCounts={orderPipelineCounts}
                dateScope={ordersDateScope}
                onDateScopeChange={setOrdersDateScope}
              />
              {hasMoreOrders && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => fetchOrders({ append: true })}
                    disabled={loadingOlderOrders}
                    className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100 disabled:opacity-50"
                  >
                    {loadingOlderOrders
                      ? ordersDateScope === 'today'
                        ? 'Loading more…'
                        : 'Loading older orders…'
                      : ordersDateScope === 'today'
                        ? 'Load more today'
                        : 'Load older orders'}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeNav === 'issues' && isSupportAdmin && (
            <OrderIssuesPanel selectedCafe={selectedCafe} />
          )}

          {activeNav === 'reports' && (
            <DineInReportPanel cafeId={selectedCafe} isSupportAdmin={isSupportAdmin} />
          )}

          {activeNav === 'reports' && (
            <BusinessReportsPanel
              cafes={cafes}
              selectedCafe={selectedCafe}
              isSupportAdmin={isSupportAdmin}
            />
          )}

          {activeNav === 'performance' && (
            <PerformanceHealthPanel
              cafes={cafes.map((c: { id: string; name: string }) => ({ id: c.id, name: c.name }))}
              selectedCafe={selectedCafe}
              storeHours={storeHoursConfig}
              activeSection={performanceSection}
              onNavigateToStoreHours={() => {
                setActiveNav('settings');
                setSettingsSection('store-hours');
                setSettingsMenuExpanded(true);
              }}
            />
          )}

          {activeNav === 'settings' && settingsSection === 'store-hours' && (
            <StoreHoursSettingsPanel
              selectedCafe={selectedCafe}
              cafeName={selectedCafeName}
              onSaved={fetchStoreHours}
            />
          )}

          {activeNav === 'customers' && (
            <CustomerInsightsPanel
              orders={orders}
              cafes={cafes.map((c: { id: string; name: string }) => ({ id: c.id, name: c.name }))}
              selectedCafe={selectedCafe}
              activeSection={customerSection}
            />
          )}

          {activeNav === 'payments' && (
            <PaymentsInfoPanel activeSection={paymentSection} />
          )}

          {activeNav === 'users' && (
            <UsersInfoPanel selectedCafe={selectedCafe} />
          )}

          {activeNav === 'home' && (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-amber-100">Live Orders</h2>
              {(
        <>
          {/* Active Orders (not completed or cancelled) */}
          <div id="home-active-orders-panel" className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <FaClipboardList className="text-xl" />
              <span>Active Orders</span>
            </h2>
            {activeOrders.length === 0 ? (
              <div className="bg-white dark:bg-[#1f1814] border border-gray-100 dark:border-[#3b2f28] rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600 dark:text-gray-300">No active orders</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.15fr_1fr]">
                <div className="bg-white dark:bg-[#1f1814] rounded-lg shadow-md border border-gray-100 dark:border-[#3b2f28] overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-[#3b2f28] bg-gray-50 dark:bg-[#201914]">
                    <p className="text-sm font-semibold text-gray-800 dark:text-amber-100">
                      {pinnedHomeOrder ? 'Opened order · live queue' : 'Active queue (newest first)'}
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      {pinnedHomeOrder
                        ? 'This ticket stays at the top. New queued orders still appear below.'
                        : 'Tap an order to preview details and actions. New tickets show up automatically.'}
                    </p>
                    {pinnedHomeOrder && (
                      <button
                        type="button"
                        onClick={() => {
                          pinHomeOrder(null);
                          void fetchOrders({ append: false, status: 'active' });
                        }}
                        className="mt-2 text-[11px] font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-700"
                      >
                        Back to live queue
                      </button>
                    )}
                  </div>
                  <div className="max-h-[34rem] overflow-y-auto">
                    {activeOrders.map((order) => {
                      const selected = selectedHomeOrder?.id === order.id;
                      return (
                        <button
                          key={order.id}
                          type="button"
                          onClick={() => {
                            setHomeSelectedOrderId(order.id);
                            if (pinnedHomeOrder && order.id !== pinnedHomeOrder.id) {
                              pinHomeOrder(null);
                            }
                          }}
                          className={`w-full border-b px-4 py-3 text-left transition ${
                            selected
                              ? 'bg-amber-50 dark:bg-[#2a2018] border-amber-200 dark:border-[#5a3a22]'
                              : 'bg-white dark:bg-[#1f1814] border-gray-100 dark:border-[#2e241d] hover:bg-gray-50 dark:hover:bg-[#271d17]'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[13px] font-bold text-amber-800 dark:text-amber-200 truncate">
                                {order.order_number || `Order #${order.id.substring(0, 8)}`}
                              </p>
                              <p className="text-[11px] text-gray-600 dark:text-gray-300 truncate">
                                {order.user_name} · {order.cafe_name}
                              </p>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                {new Date(order.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${getStatusColor(order.status)}`}
                              >
                                {getOrderStatusDisplay(order).fullLabel}
                              </span>
                              <p className="mt-1 text-[13px] font-bold text-amber-700">
                                {formatCurrency(order.total_amount)}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedHomeOrder && (
                  <div className="bg-white dark:bg-[#1f1814] rounded-lg shadow-md border border-gray-100 dark:border-[#3b2f28] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-amber-800 dark:text-amber-200 truncate">
                          {selectedHomeOrder.order_number || `Order #${selectedHomeOrder.id.substring(0, 8)}`}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {selectedHomeOrder.cafe_name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Customer: {selectedHomeOrder.user_name} ({selectedHomeOrder.user_email})
                        </p>
                        {selectedHomeOrder.user_phone && (
                          <p className="text-sm text-gray-600 dark:text-gray-300">Phone: {selectedHomeOrder.user_phone}</p>
                        )}
                        <DeliveryOrderSummary
                          order={selectedHomeOrder}
                          className="text-sm text-amber-800 dark:text-amber-200 mt-1"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(selectedHomeOrder.created_at).toLocaleString()}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            {selectedHomeOrder.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)} item(s)
                          </span>
                          {selectedHomeOrder.payment_method && (
                            <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-200">
                              {formatPaymentMethodLabel(selectedHomeOrder.payment_method)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${getStatusColor(
                            selectedHomeOrder.status
                          )}`}
                        >
                          {getOrderStatusDisplay(selectedHomeOrder).fullLabel}
                        </span>
                        <p className="mt-2 text-2xl font-bold text-amber-700">
                          {formatCurrency(selectedHomeOrder.total_amount)}
                        </p>
                      </div>
                    </div>

                    {pinnedHomeOrder && selectedHomeOrder.id === pinnedHomeOrder.id && (
                      <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        This ticket stays on screen until you mark it received, cancel it, or go back to the live queue.
                      </p>
                    )}

                    <div className="mt-4 border-t pt-4">
                      <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-100">Items</h4>
                      <ul className="space-y-2">
                        {selectedHomeOrder.items.map((item, idx) => (
                          <li key={idx} className="text-gray-700 dark:text-gray-200 text-sm">
                            <div>
                              <span className="font-semibold">
                                {item.quantity}x {item.name || `Item #${item.menu_item_id.substring(0, 8)}`}
                              </span>
                            </div>
                            {item.customizations && (
                              <div className="ml-4 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                                {(Object.entries(item.customizations) as [string, string][]).map(([key, value]) => (
                                  <p key={key}>
                                    <span className="font-semibold">
                                      {key === 'size'
                                        ? 'Cup size'
                                        : key === 'milk'
                                        ? 'Milk'
                                        : key === 'shots'
                                        ? 'Shots'
                                        : key === 'sweetness'
                                        ? 'Sweetness'
                                        : key === 'ice'
                                        ? 'Ice'
                                        : key === 'topping'
                                        ? 'Topping'
                                        : key}
                                      :
                                    </span>{' '}
                                    {value.replace(/-/g, ' ')}
                                  </p>
                                ))}
                              </div>
                            )}
                            {item.note && (
                              <p className="ml-4 text-xs text-amber-700 dark:text-amber-300 italic">Note: {item.note}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {selectedHomeOrder.order_number && (
                      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Ref: {selectedHomeOrder.order_number}</p>
                    )}
                    {selectedHomeOrder.payment_method && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                        Payment: {formatPaymentMethodLabel(selectedHomeOrder.payment_method)}
                      </p>
                    )}
                    {selectedHomeOrder.name && (
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">Note: {selectedHomeOrder.name}</p>
                    )}

                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-[#3b2f28]">
                      {getStatusButtons(selectedHomeOrder.status, selectedHomeOrder.id)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          </>
            )}
            </div>
          )}

          {(activeNav === 'menu' || (activeNav === 'home' && activeTab === 'menu')) && (
        <div className="mt-4">
          <h2 className="text-2xl font-bold mb-4 text-amber-700 flex items-center gap-2">
            <FaUtensils className="text-xl" />
            <span>Menu & Availability</span>
          </h2>
          {selectedCafe && !menuLoading && (
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <h3 className="text-lg font-semibold text-amber-800 mb-3">Add Menu Item</h3>
              <form
                className="grid md:grid-cols-2 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newMenuItem.name.trim() || !newMenuItem.category.trim() || Number(newMenuItem.price) <= 0) {
                    showToast('Please enter item name, category, and a valid price', 'error');
                    return;
                  }
                  try {
                    await axios.post(`/api/cafes/${selectedCafe}/menu`, {
                      name: newMenuItem.name.trim(),
                      description: newMenuItem.description.trim(),
                      category: newMenuItem.category.trim(),
                      price: customerTotalFromCafePrice(
                        Number(newMenuItem.price),
                        menuPlatformFeePct
                      ),
                      image_url: newMenuItem.image_url.trim() || undefined,
                      customization_config: newMenuItem.customization_config,
                    });
                    setNewMenuItem({
                      name: '',
                      description: '',
                      category: 'Coffee',
                      price: 1.5,
                      image_url: '',
                      customization_config: suggestCustomizationConfig({
                        category: 'Coffee',
                        name: '',
                      }),
                    });
                    await fetchMenu();
                    showToast('Menu item created', 'success');
                  } catch (error: any) {
                    console.error('Error creating menu item:', error);
                    showToast(error.response?.data?.error || 'Failed to create menu item', 'error');
                  }
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                    value={newMenuItem.name}
                    onChange={(e) => setNewMenuItem((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Spanish Latte"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                    value={newMenuItem.category}
                    onChange={(e) => setNewMenuItem((prev) => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Coffee"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                    value={newMenuItem.description}
                    onChange={(e) => setNewMenuItem((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Short description for customers"
                  />
                </div>
                <MenuPriceFields
                  cafePrice={newMenuItem.price}
                  onCafePriceChange={(value) =>
                    setNewMenuItem((prev) => ({ ...prev, price: value }))
                  }
                  platformFeePct={menuPlatformFeePct}
                  imageUrl={newMenuItem.image_url}
                  onImageUrlChange={(value) =>
                    setNewMenuItem((prev) => ({ ...prev, image_url: value }))
                  }
                />
                <MenuCustomizationPicker
                  config={newMenuItem.customization_config}
                  onChange={(cfg) =>
                    setNewMenuItem((prev) => ({ ...prev, customization_config: cfg }))
                  }
                  category={newMenuItem.category}
                  name={newMenuItem.name}
                />
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-amber-700 text-white rounded hover:bg-amber-800 text-sm font-semibold"
                  >
                    Add Item
                  </button>
                </div>
              </form>
            </div>
          )}

          <p className="text-sm text-gray-600 mb-6 max-w-3xl">
            Add or edit items for the selected café. Use <span className="font-semibold">Mark sold out</span>{' '}
            when an item is temporarily unavailable.
          </p>

          {!selectedCafe && (
            <p className="text-sm text-gray-600 mb-4">
              Select a specific cafe above to manage its menu availability.
            </p>
          )}
          {selectedCafe && menuLoading && (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="text-gray-600">Loading menu...</p>
            </div>
          )}
          {selectedCafe && !menuLoading && editingMenuItem && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-md p-4 mb-4">
              <h3 className="text-lg font-semibold text-amber-900 mb-3">
                Edit: {editingMenuItem.name}
              </h3>
              <form
                className="grid md:grid-cols-2 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!selectedCafe) return;
                  try {
                    await axios.put(
                      `/api/cafes/${selectedCafe}/menu/${editingMenuItem.id}`,
                      {
                        name: editingMenuItem.name.trim(),
                        description: editingMenuItem.description.trim(),
                        category: editingMenuItem.category.trim(),
                        price: customerTotalFromCafePrice(
                          Number(editingMenuItem.price),
                          menuPlatformFeePct
                        ),
                        image_url: editingMenuItem.image_url.trim() || null,
                        customization_config: editingMenuItem.customization_config,
                      }
                    );
                    await fetchMenu();
                    setEditingMenuItem(null);
                    showToast('Menu item updated', 'success');
                  } catch (error: any) {
                    showToast(error.response?.data?.error || 'Failed to update item', 'error');
                  }
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded"
                    value={editingMenuItem.name}
                    onChange={(e) =>
                      setEditingMenuItem((prev) =>
                        prev ? { ...prev, name: e.target.value } : prev
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded"
                    value={editingMenuItem.category}
                    onChange={(e) =>
                      setEditingMenuItem((prev) =>
                        prev ? { ...prev, category: e.target.value } : prev
                      )
                    }
                  />
                </div>
                <MenuPriceFields
                  cafePrice={editingMenuItem.price}
                  onCafePriceChange={(value) =>
                    setEditingMenuItem((prev) => (prev ? { ...prev, price: value } : prev))
                  }
                  platformFeePct={menuPlatformFeePct}
                  imageUrl={editingMenuItem.image_url}
                  onImageUrlChange={(value) =>
                    setEditingMenuItem((prev) =>
                      prev ? { ...prev, image_url: value } : prev
                    )
                  }
                />
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    className="w-full px-3 py-2 border rounded"
                    value={editingMenuItem.description}
                    onChange={(e) =>
                      setEditingMenuItem((prev) =>
                        prev ? { ...prev, description: e.target.value } : prev
                      )
                    }
                  />
                </div>
                <MenuCustomizationPicker
                  config={editingMenuItem.customization_config}
                  onChange={(cfg) =>
                    setEditingMenuItem((prev) =>
                      prev ? { ...prev, customization_config: cfg } : prev
                    )
                  }
                  category={editingMenuItem.category}
                  name={editingMenuItem.name}
                />
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingMenuItem(null)}
                    className="px-4 py-2 border rounded text-sm font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-amber-700 text-white rounded text-sm font-semibold"
                  >
                    Save changes
                  </button>
                </div>
              </form>
            </div>
          )}

          {selectedCafe && !menuLoading && (
            <>
            <div className="bg-white rounded-lg shadow-md p-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-left py-2 px-2">Item</th>
                    <th className="text-left py-2 px-2">Customer price</th>
                    <th className="text-left py-2 px-2">Customer options</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {menuItems.map(item => {
                    const optionSummary = formatCustomizationConfigSummary(
                      configFromApiItem(item)
                    );
                    return (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="py-2 px-2 text-gray-600">{item.category}</td>
                      <td className="py-2 px-2">
                        <div className="font-semibold text-gray-800">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
                        )}
                      </td>
                      <td className="py-2 px-2 text-gray-700">{formatCurrency(item.price)}</td>
                      <td className="py-2 px-2 text-xs text-gray-600 max-w-[10rem]">{optionSummary}</td>
                      <td className="py-2 px-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            item.is_available
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {item.is_available ? 'Available' : 'Sold out'}
                        </span>
                      </td>
                      <td className="py-2 px-2 space-y-1">
                        <button
                          type="button"
                          onClick={() => {
                            const cfg = configFromApiItem(item);
                            const hasConfig = Object.keys(cfg).length > 0;
                            setEditingMenuItem({
                              id: item.id,
                              name: item.name,
                              description: item.description || '',
                              category: item.category || '',
                              price: cafePriceFromCustomerTotal(
                                item.price,
                                menuPlatformFeePct
                              ),
                              image_url: item.image_url || '',
                              customization_config: hasConfig
                                ? { ...cfg }
                                : suggestCustomizationConfig({
                                    category: item.category || '',
                                    name: item.name || '',
                                  }),
                            });
                          }}
                          className="block px-3 py-1 rounded-full text-xs font-semibold border border-gray-400 text-gray-800 hover:bg-gray-50"
                        >
                          Edit options
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const newValue = !item.is_available;
                              await axios.put(`/api/cafes/${selectedCafe}/menu/${item.id}/availability`, {
                                is_available: newValue,
                              });
                              setMenuItems(prev =>
                                prev.map(m =>
                                  m.id === item.id ? { ...m, is_available: newValue } : m
                                )
                              );
                              showToast(
                                newValue
                                  ? `"${item.name}" marked as available`
                                  : `"${item.name}" marked as sold out`,
                                'success'
                              );
                            } catch (error: any) {
                              console.error('Error updating availability:', error);
                              showToast(
                                error.response?.data?.error || 'Failed to update availability',
                                'error'
                              );
                            }
                          }}
                          className="block px-3 py-1 rounded-full text-xs font-semibold border border-amber-500 text-amber-700 hover:bg-amber-50"
                        >
                          {item.is_available ? 'Mark sold out' : 'Mark available'}
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

          {activeNav === 'marketing' && (
        <div className="mt-4">
          <h2 className="text-2xl font-bold mb-4 text-amber-700 flex items-center gap-2">
            <FaBullhorn className="text-xl" />
            <span>Marketing</span>
          </h2>
          <p className="text-sm text-gray-600 mb-4 max-w-3xl">
            Key areas to focus on—pick a topic from the Marketing menu in the sidebar to jump
            straight to it.
          </p>

          <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Create campaign or offer">
            <button
              type="button"
              role="tab"
              aria-selected={!editingPromoId && newPromo.promotion_type === 'campaign'}
              onClick={() => {
                setEditingPromoId(null);
                setNewPromo(emptyPromoForm('campaign'));
              }}
              className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                !editingPromoId && newPromo.promotion_type === 'campaign'
                  ? 'bg-amber-700 text-white shadow-md'
                  : 'border border-amber-200 bg-white text-amber-900 hover:bg-amber-50'
              }`}
            >
              New campaign
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!editingPromoId && newPromo.promotion_type === 'offer'}
              onClick={() => {
                setEditingPromoId(null);
                setNewPromo(emptyPromoForm('offer'));
              }}
              className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                !editingPromoId && newPromo.promotion_type === 'offer'
                  ? 'bg-amber-700 text-white shadow-md'
                  : 'border border-amber-200 bg-white text-amber-900 hover:bg-amber-50'
              }`}
            >
              New offer
            </button>
          </div>

          {newPromo.promotion_type !== 'offer' && (
          <div
            id="mkt-budget-planner"
            className="rounded-2xl border border-[#e0d0c0] bg-gradient-to-br from-[#fff8f0] via-[#fffcf7] to-[#f5ebe0]/60 p-5 shadow-sm mb-6 scroll-mt-24"
          >
            <h3 className="text-base font-bold text-amber-900 mb-1">Monthly marketing budget planner</h3>
            <p className="text-xs text-[#6b5f57] mb-4">
              Plan tier for this café · feed and homepage each have their own daily rate
            </p>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-800/80 mb-2">
              Budget tier
            </p>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 mb-4"
              role="radiogroup"
              aria-label="Marketing budget tier"
            >
              {(['starter', 'growth', 'pro', 'uncapped'] as const).map((tier) => {
                const plan = MARKETING_BUDGET_PLANS[tier];
                const selected = marketingBudgetTier === tier;
                return (
                  <button
                    key={tier}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={qhShell.marketingTierCard(selected)}
                    onClick={() => saveMarketingBudgetTier(tier)}
                  >
                    <span className="block text-sm font-bold text-[#3d2918]">{plan.label}</span>
                    <span className="mt-1 block text-xs font-semibold text-amber-900">
                      {plan.monthlyBudget == null
                        ? 'No monthly cap'
                        : `${plan.monthlyBudget} JOD / month`}
                    </span>
                    <span className="mt-1 block text-[11px] text-gray-600 leading-snug">
                      {plan.campaignLimit == null
                        ? 'Unlimited campaigns'
                        : `Up to ${plan.campaignLimit} campaigns`}
                    </span>
                    <span className="mt-1 block text-[11px] text-gray-600 leading-snug">
                      Feed {plan.campaignDaily.toFixed(2)} · Home{' '}
                      {plan.campaignDailyHomepage.toFixed(2)} JOD/day
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-[#e8dcc8] pt-4 text-sm">
              <p className="text-gray-700">
                Monthly cap:{' '}
                <span className="font-semibold text-[#3d2918]">
                  {selectedMarketingPlan.monthlyBudget == null
                    ? 'Uncapped'
                    : `${selectedMarketingPlan.monthlyBudget.toFixed(2)} JOD`}
                </span>
              </p>
              <p className="text-gray-700">
                Campaign limit:{' '}
                <span className="font-semibold text-[#3d2918]">
                  {selectedMarketingPlan.campaignLimit == null
                    ? 'Unlimited'
                    : `${selectedMarketingPlan.campaignLimit} campaigns`}
                </span>
              </p>
              {selectedCafe && campaignUsage && (
                <>
                  <p className="text-gray-700">
                    Active campaigns:{' '}
                    <span
                      className={`font-semibold ${
                        !campaignUsage.can_create_campaign ? 'text-red-600' : 'text-emerald-700'
                      }`}
                    >
                      {campaignUsage.active_campaign_count}
                      {campaignUsage.campaign_limit != null
                        ? ` / ${campaignUsage.campaign_limit}`
                        : ' (unlimited plan)'}
                    </span>
                  </p>
                  <p className="text-gray-700">
                    Homepage featured:{' '}
                    <span
                      className={`font-semibold ${
                        !campaignUsage.can_create_homepage_campaign
                          ? 'text-red-600'
                          : 'text-emerald-700'
                      }`}
                    >
                      {campaignUsage.homepage_active_count} / {campaignUsage.homepage_slot_limit}
                    </span>
                    <span className="text-xs text-gray-500">
                      {' '}
                      · this café {campaignUsage.homepage_cafe_active_count} /{' '}
                      {campaignUsage.homepage_per_cafe_limit}
                    </span>
                  </p>
                </>
              )}
              {!selectedCafe && (
                <p id="marketing-tier-hint" className="text-xs text-amber-800 w-full">
                  Pick a café under Location to save tier and enforce limits.
                </p>
              )}
              <div className="w-full sm:w-auto sm:ml-auto sm:text-right">
                <p className="text-gray-700">
                  This campaign run:{' '}
                  <span
                    className={`font-bold tabular-nums ${
                      selectedMarketingPlan.monthlyBudget != null &&
                      estimatedPromoCost > selectedMarketingPlan.monthlyBudget
                        ? 'text-red-600'
                        : 'text-emerald-700'
                    }`}
                  >
                    {estimatedPromoCost.toFixed(2)} JOD
                  </span>
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {promoDurationDays > 0
                    ? `${promoDurationDays} day${promoDurationDays === 1 ? '' : 's'} × ${selectedCampaignDaily.toFixed(2)} JOD/day${
                        newPromo.promotion_type === 'campaign'
                          ? newPromo.campaign_placement === 'homepage'
                            ? ' (homepage)'
                            : ' (feed)'
                          : ''
                      }`
                    : 'Set start & end dates below to calculate'}
                </p>
              </div>
            </div>
          </div>
          )}

          {marketingSection === 'campaigns-offers' && !selectedCafe && (
            <p className="mb-4 text-sm text-amber-900">
              Pick a café under Location to set the dine-in discount.
            </p>
          )}

          {marketingSection === 'campaigns-offers' && selectedCafe && (
            <DineInDiscountCard
              cafeId={selectedCafe}
              cafeName={selectedCafeName}
              isSupportAdmin={isSupportAdmin}
              showToast={showToast}
            />
          )}

          {marketingSection === 'campaigns-offers' && (
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 mb-8">
              <h3 className="text-base font-bold text-amber-800 mb-4">Campaigns &amp; offers</h3>
              <div className="space-y-5 text-sm text-gray-700">
                <div>
                  <p className="font-semibold text-gray-900">Campaigns (ads)</p>
                  <p className="mt-1 leading-relaxed">
                    Pay by the day to move this café up. Feed boost ranks the café in the list.
                    Homepage featured costs more because only 8 cafés can sit in that row at once
                    (1 per café). A campaign does not change the drink price.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Offers (deals)</p>
                  <p className="mt-1 leading-relaxed">
                    The discount at checkout: percent off, buy 1 get 1 free, or 50%/25% off the
                    second drink. Choose whether it applies to drinks only, food only, or the whole
                    order, then{' '}
                    <span className="font-medium">basic</span> listing or{' '}
                    <span className="font-medium">highlighted</span> for homepage placement of that
                    deal. The setup fee is a monthly rate, so two weeks costs half a month, not the
                    full 17 or 7 JOD.
                  </p>
                </div>
              </div>
            </div>
          )}

          {marketingSection === 'performance-tracking' && (
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 mb-8">
              <h3 className="text-base font-bold text-amber-800 mb-4">Performance tracking</h3>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 leading-relaxed">
                <li>
                  <MarketingJumpLink onClick={() => jumpToMarketingAnchor('mkt-promotions-list')}>
                    View real-time performance data for both ads and offers from a unified reporting
                    dashboard.
                  </MarketingJumpLink>
                </li>
                <li>
                  <MarketingJumpLink onClick={jumpToMarketingReports}>
                    Track orders and revenue during each campaign or offer from Reports.
                  </MarketingJumpLink>
                </li>
                <li>
                  <MarketingJumpLink onClick={jumpToBudgetPlanner}>
                    Use insights to optimize campaigns—identify what&apos;s working and adjust
                    budgets or targeting accordingly.
                  </MarketingJumpLink>
                </li>
              </ul>
            </div>
          )}

          {marketingSection === 'campaign-management' && (
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 mb-8">
              <h3 className="text-base font-bold text-amber-800 mb-4">Campaign management</h3>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 leading-relaxed">
                <li>
                  <MarketingJumpLink onClick={() => jumpToMarketingAnchor('mkt-promotions-list')}>
                    <span className="font-semibold text-gray-900">All campaigns page:</span> see all
                    past, active, and planned offers and ads in one place.
                  </MarketingJumpLink>
                </li>
                <li>
                  <MarketingJumpLink onClick={() => jumpToMarketingAnchor('mkt-promotions-list')}>
                    <span className="font-semibold text-gray-900">Edit, clone, or pause:</span>{' '}
                    modify active campaigns, duplicate successful ones, or pause underperforming ones
                    instantly.
                  </MarketingJumpLink>
                </li>
                <li>
                  <MarketingJumpLink onClick={jumpToMultiStorePromotions}>
                    <span className="font-semibold text-gray-900">Multi-location insights:</span>{' '}
                    monitor performance across multiple stores and timeframes from a single view.
                  </MarketingJumpLink>
                </li>
              </ul>
            </div>
          )}

          {/* Create promotion form + list — always on Marketing */}
          <div id="mkt-create-promo" className="bg-white rounded-lg shadow-md p-6 mb-6 scroll-mt-24">
            <h3 className="text-lg font-bold text-amber-800 mb-4">
              {editingPromoId
                ? newPromo.promotion_type === 'offer'
                  ? 'Edit offer'
                  : 'Edit campaign'
                : newPromo.promotion_type === 'offer'
                  ? 'Create new offer'
                  : 'Create new campaign'}
            </h3>
            {!selectedCafe && (
              <p className="text-sm text-gray-600 mb-4">
                Choose a café under <span className="font-semibold">Location</span> in the sidebar to
                create a promotion for that store.
              </p>
            )}
            {selectedCafe && (
              <form
                className="grid md:grid-cols-2 gap-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newPromo.title.trim() || !newPromo.description.trim() || !newPromo.valid_from || !newPromo.valid_until) {
                    showToast('Please fill in all fields', 'error');
                    return;
                  }
                  if (promoDurationDays <= 0) {
                    showToast('End date must be after start date', 'error');
                    return;
                  }
                  const budgetTotal = Number(newPromo.budget_total_jod);
                  if (!Number.isFinite(budgetTotal) || budgetTotal <= 0) {
                    showToast('Enter a total amount (JOD) greater than zero', 'error');
                    return;
                  }
                  if (
                    estimatedPromoCost > 0 &&
                    selectedMarketingPlan.monthlyBudget != null &&
                    estimatedPromoCost > selectedMarketingPlan.monthlyBudget
                  ) {
                    showToast(
                      `These dates cost ${estimatedPromoCost.toFixed(2)} JOD, which exceeds your monthly budget cap (${selectedMarketingPlan.monthlyBudget.toFixed(2)} JOD). Shorten the dates instead of lowering the total.`,
                      'error'
                    );
                    return;
                  }
                  if (estimatedPromoCost > 0 && budgetTotal + 0.01 < estimatedPromoCost) {
                    showToast(
                      `Total amount (${budgetTotal.toFixed(2)} JOD) is below the required ${estimatedPromoCost.toFixed(2)} JOD for these dates. Shorten the dates if you need a lower total.`,
                      'error'
                    );
                    return;
                  }
                  if (
                    selectedMarketingPlan.monthlyBudget != null &&
                    budgetTotal > selectedMarketingPlan.monthlyBudget
                  ) {
                    showToast(
                      `Total amount (${budgetTotal.toFixed(2)} JOD) exceeds your monthly budget cap (${selectedMarketingPlan.monthlyBudget.toFixed(2)} JOD).`,
                      'error'
                    );
                    return;
                  }
                  if (
                    !editingPromoId &&
                    newPromo.promotion_type === 'campaign' &&
                    campaignUsage &&
                    !campaignUsage.can_create_campaign
                  ) {
                    showToast(
                      `Campaign limit reached (${campaignUsage.active_campaign_count} active). Pause a campaign or upgrade your marketing tier.`,
                      'error'
                    );
                    return;
                  }
                  if (
                    !editingPromoId &&
                    newPromo.promotion_type === 'campaign' &&
                    newPromo.campaign_placement === 'homepage' &&
                    campaignUsage &&
                    !campaignUsage.can_create_homepage_campaign
                  ) {
                    showToast(
                      campaignUsage.homepage_cafe_active_count >=
                        campaignUsage.homepage_per_cafe_limit
                        ? 'This café already has a homepage featured campaign. Pause it or pick Feed boost.'
                        : `Homepage featured is full (${campaignUsage.homepage_active_count}/${campaignUsage.homepage_slot_limit} cafés). Choose Feed boost or wait for a slot.`,
                      'error'
                    );
                    return;
                  }
                  try {
                    const setupFeeMonthly =
                      newPromo.promotion_type === 'offer'
                        ? Math.max(0, Number(newPromo.setup_fee_monthly || 0))
                        : 0;
                    const isOffer = newPromo.promotion_type === 'offer';
                    const isBogoDeal = isOffer && newPromo.deal !== 'percent';
                    const payload = {
                      cafe_id: selectedCafe,
                      promotion_type: newPromo.promotion_type,
                      offer_tier: newPromo.offer_tier,
                      title: newPromo.title.trim(),
                      description: newPromo.description.trim(),
                      deal_type: isOffer ? (isBogoDeal ? 'bogo' : 'percent') : 'none',
                      bogo_off_percent: isBogoDeal
                        ? newPromo.deal === 'bogo_50'
                          ? 50
                          : newPromo.deal === 'bogo_25'
                            ? 25
                            : 100
                        : null,
                      discount_percentage: isOffer && !isBogoDeal ? newPromo.discount_percentage : null,
                      campaign_audience: isOffer ? null : newPromo.campaign_audience,
                      campaign_placement: isOffer ? null : newPromo.campaign_placement,
                      offer_applies_to: isOffer ? newPromo.offer_applies_to : null,
                      setup_fee_monthly: setupFeeMonthly,
                      performance_addon_enabled:
                        newPromo.promotion_type === 'campaign'
                          ? !!newPromo.performance_addon_enabled
                          : false,
                      performance_fee_per_redeemed_order:
                        newPromo.promotion_type === 'campaign' && newPromo.performance_addon_enabled
                          ? Math.min(0.05, Math.max(0.03, Number(newPromo.performance_fee_per_redeemed_order || 0.03)))
                          : 0,
                      valid_from: newPromo.valid_from,
                      valid_until: newPromo.valid_until,
                      daily_start_time: newPromo.restrict_daily_hours
                        ? newPromo.daily_start_time
                        : null,
                      daily_end_time: newPromo.restrict_daily_hours
                        ? newPromo.daily_end_time
                        : null,
                      budget_total_jod: budgetTotal,
                      active: true,
                    };
                    const response = editingPromoId
                      ? await axios.put(`/api/promotions/${editingPromoId}`, payload)
                      : await axios.post('/api/promotions', payload);
                    const saved = response.data;
                    setPromotions((prev) =>
                      editingPromoId
                        ? prev.map((p) => (p.id === editingPromoId ? { ...p, ...saved } : p))
                        : [saved, ...prev]
                    );
                    fetchCampaignUsage();
                    setEditingPromoId(null);
                    setNewPromo(emptyPromoForm('campaign'));
                    showToast(
                      editingPromoId
                        ? `${payload.promotion_type === 'offer' ? 'Offer' : 'Campaign'} updated`
                        : `${payload.promotion_type === 'offer' ? 'Offer' : 'Campaign'} created and activated`,
                      'success'
                    );
                  } catch (error: any) {
                    console.error('Error saving promotion:', error);
                    const apiErr = error.response?.data?.error;
                    showToast(apiErr || 'Failed to save promotion', 'error');
                    if (error.response?.status === 403) {
                      fetchCampaignUsage();
                    }
                  }
                }}
              >
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                      value={newPromo.title}
                      onChange={(e) => setNewPromo(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={
                        newPromo.promotion_type === 'offer'
                          ? 'e.g., Morning Latte 20% Off'
                          : 'e.g., Weekend boost for Nomad'
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                      rows={3}
                      value={newPromo.description}
                      onChange={(e) => setNewPromo(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={
                        newPromo.promotion_type === 'offer'
                          ? 'e.g., Get 20% off all lattes from 8–11 AM.'
                          : 'e.g., Appear higher in the café list this weekend.'
                      }
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {newPromo.promotion_type === 'campaign' && (
                    <>
                      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        This does not change the drink price. It only moves the café up.
                      </p>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Who should see this
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          value={newPromo.campaign_audience}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              campaign_audience: e.target.value as typeof prev.campaign_audience,
                            }))
                          }
                        >
                          <option value="all">{CAMPAIGN_AUDIENCE_OPTIONS.all.label}</option>
                          <option value="new">{CAMPAIGN_AUDIENCE_OPTIONS.new.label}</option>
                          <option value="returning">{CAMPAIGN_AUDIENCE_OPTIONS.returning.label}</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {CAMPAIGN_AUDIENCE_OPTIONS[newPromo.campaign_audience].hint}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Where it shows
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          value={newPromo.campaign_placement}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              campaign_placement: e.target.value as typeof prev.campaign_placement,
                            }))
                          }
                        >
                          <option value="feed">
                            {CAMPAIGN_PLACEMENT_OPTIONS.feed.label} —{' '}
                            {selectedMarketingPlan.campaignDaily.toFixed(2)} JOD/day
                          </option>
                          <option value="homepage">
                            {CAMPAIGN_PLACEMENT_OPTIONS.homepage.label} —{' '}
                            {selectedMarketingPlan.campaignDailyHomepage.toFixed(2)} JOD/day
                          </option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {CAMPAIGN_PLACEMENT_OPTIONS[newPromo.campaign_placement].hint}
                        </p>
                      </div>
                    </>
                  )}
                  {newPromo.promotion_type === 'offer' && (
                    <>
                      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        This is the discount at checkout. Highlighted only buys a better listing for
                        that deal.
                      </p>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Deal</label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          value={newPromo.deal}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              deal: e.target.value as typeof prev.deal,
                            }))
                          }
                        >
                          <option value="percent">Percent off</option>
                          <option value="bogo_100">Buy 1, get 1 free</option>
                          <option value="bogo_50">Buy 1, get 1 50% off</option>
                          <option value="bogo_25">Buy 1, get 1 25% off</option>
                        </select>
                        {newPromo.deal !== 'percent' && (
                          <p className="text-xs text-gray-500 mt-1">
                            {newPromo.offer_applies_to === 'drinks'
                              ? 'Pairs drinks only. The cheaper drink in each pair is discounted. Food does not count.'
                              : newPromo.offer_applies_to === 'food'
                                ? 'Pairs food only (sweets, pastries, savory). The cheaper food item in each pair is discounted. Drinks do not count.'
                                : 'Customer pays the higher-priced item. The cheaper item in each pair is discounted — mixed items work.'}{' '}
                            Needs 2 or more qualifying items.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Applies to
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          value={newPromo.offer_applies_to}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              offer_applies_to: e.target.value as typeof prev.offer_applies_to,
                            }))
                          }
                        >
                          <option value="drinks">{OFFER_APPLIES_OPTIONS.drinks.label}</option>
                          <option value="food">{OFFER_APPLIES_OPTIONS.food.label}</option>
                          <option value="all">{OFFER_APPLIES_OPTIONS.all.label}</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {OFFER_APPLIES_OPTIONS[newPromo.offer_applies_to].hint}
                        </p>
                      </div>
                      {newPromo.deal === 'percent' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Discount (%)</label>
                          <input
                            type="number"
                            min={1}
                            max={90}
                            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                            value={newPromo.discount_percentage}
                            onChange={(e) => setNewPromo(prev => ({
                              ...prev,
                              discount_percentage: Number(e.target.value),
                            }))}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {newPromo.promotion_type === 'offer' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Offer tier</label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          value={newPromo.offer_tier}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              offer_tier: e.target.value as 'basic' | 'highlighted',
                              setup_fee_monthly: e.target.value === 'highlighted' ? 17 : 7,
                            }))
                          }
                        >
                          <option value="basic">{OFFER_TIER_OPTIONS.basic.label}</option>
                          <option value="highlighted">{OFFER_TIER_OPTIONS.highlighted.label}</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {OFFER_TIER_OPTIONS[newPromo.offer_tier].hint}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Setup fee (JOD per month)
                        </label>
                        <input
                          type="number"
                          min={newPromo.offer_tier === 'highlighted' ? 15 : 5}
                          max={newPromo.offer_tier === 'highlighted' ? 20 : 10}
                          step={0.01}
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          value={newPromo.setup_fee_monthly}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              setup_fee_monthly: Number(e.target.value),
                            }))
                          }
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Recommended range:{' '}
                          {newPromo.offer_tier === 'highlighted' ? '15-20 JOD' : '5-10 JOD'}
                          . Total below is this rate × days ÷ {OFFER_BILLING_DAYS_PER_MONTH}.
                        </p>
                      </div>
                    </>
                  )}
                  {newPromo.promotion_type === 'campaign' && (
                    <div className="rounded-md border border-amber-200 p-3 bg-amber-50/50">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={newPromo.performance_addon_enabled}
                          onChange={(e) =>
                            setNewPromo((prev) => ({
                              ...prev,
                              performance_addon_enabled: e.target.checked,
                              performance_fee_per_redeemed_order: e.target.checked
                                ? prev.performance_fee_per_redeemed_order || selectedMarketingPlan.defaultPerformanceFee
                                : 0,
                            }))
                          }
                        />
                        Enable performance add-on
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Add a fee per redeemed campaign order on top of normal Qahwahub fees.
                      </p>
                      <input
                        type="number"
                        min={0.03}
                        max={0.05}
                        step={0.01}
                        disabled={!newPromo.performance_addon_enabled}
                        className="mt-2 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100 disabled:text-gray-400"
                        value={newPromo.performance_fee_per_redeemed_order}
                        onChange={(e) =>
                          setNewPromo((prev) => ({
                            ...prev,
                            performance_fee_per_redeemed_order: Number(e.target.value),
                          }))
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">Recommended range: 0.03-0.05 JOD per redeemed order.</p>
                    </div>
                  )}
                  <div className="rounded-md border border-amber-100 bg-amber-50/30 p-3 space-y-3">
                    <label className="flex items-start gap-2 text-sm font-semibold text-gray-800">
                      <input
                        type="checkbox"
                        checked={newPromo.restrict_daily_hours}
                        onChange={(e) =>
                          setNewPromo((prev) => ({
                            ...prev,
                            restrict_daily_hours: e.target.checked,
                          }))
                        }
                        className="mt-1"
                      />
                      <span>
                        Limit to hours each day (Jordan time)
                        <span className="block text-xs font-normal text-gray-500 mt-0.5">
                          {newPromo.promotion_type === 'offer'
                            ? 'Outside these hours the discount is hidden and cannot be applied at checkout.'
                            : 'Outside these hours this ad is paused and the café is not boosted.'}
                        </span>
                      </span>
                    </label>
                    {newPromo.restrict_daily_hours && (
                      <div className="grid grid-cols-2 gap-3 pl-6">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            From
                          </label>
                          <input
                            type="time"
                            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                            value={newPromo.daily_start_time}
                            onChange={(e) =>
                              setNewPromo((prev) => ({
                                ...prev,
                                daily_start_time: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Until
                          </label>
                          <input
                            type="time"
                            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                            value={newPromo.daily_end_time}
                            onChange={(e) =>
                              setNewPromo((prev) => ({
                                ...prev,
                                daily_end_time: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Start</label>
                      <DateTimeSplitInput
                        value={newPromo.valid_from}
                        fallbackTime="09:00"
                        dateLabel="Date"
                        timeLabel="Time"
                        onChange={(next) => setNewPromo((prev) => ({ ...prev, valid_from: next }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">End</label>
                      <DateTimeSplitInput
                        value={newPromo.valid_until}
                        fallbackTime="21:00"
                        dateLabel="Date"
                        timeLabel="Time"
                        onChange={(next) => setNewPromo((prev) => ({ ...prev, valid_until: next }))}
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">
                        Total amount (JOD)
                      </label>
                      <input
                        type="number"
                        min={estimatedPromoCost > 0 ? estimatedPromoCost : 0.01}
                        step={0.01}
                        className="w-full max-w-xs px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500 text-lg font-semibold tabular-nums"
                        value={newPromo.budget_total_jod || ''}
                        onChange={(e) =>
                          setNewPromo((prev) => ({
                            ...prev,
                            budget_total_jod: Number(e.target.value),
                          }))
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {newPromo.promotion_type === 'campaign' ? (
                          <>
                            Required: {estimatedPromoCost.toFixed(2)} JOD (
                            {promoDurationDays} day{promoDurationDays !== 1 ? 's' : ''} ×{' '}
                            {selectedCampaignDaily.toFixed(2)} JOD/day{' '}
                            {newPromo.campaign_placement === 'homepage'
                              ? 'homepage'
                              : 'feed'}
                            ). You can pay more, but not less — shorten the dates for a lower total.
                          </>
                        ) : (
                          <>
                            {promoDurationDays > 0 ? (
                              <>
                                Required: {estimatedPromoCost.toFixed(2)} JOD (
                                {Number(newPromo.setup_fee_monthly || 0).toFixed(2)} JOD/month ×{' '}
                                {promoDurationDays} / {OFFER_BILLING_DAYS_PER_MONTH} days). A shorter
                                run costs less than a full month.
                              </>
                            ) : (
                              <>
                                Set start and end dates to prorate the monthly setup fee (
                                {Number(newPromo.setup_fee_monthly || 0).toFixed(2)} JOD).
                              </>
                            )}
                          </>
                        )}
                      </p>
                      {estimatedPromoCost > 0 &&
                        selectedMarketingPlan.monthlyBudget != null &&
                        estimatedPromoCost > selectedMarketingPlan.monthlyBudget && (
                          <p className="text-xs text-red-700 mt-1">
                            These dates cost {estimatedPromoCost.toFixed(2)} JOD, above your{' '}
                            {selectedMarketingPlan.monthlyBudget.toFixed(2)} JOD monthly cap.
                            Shorten the dates — typing a lower total will not launch the campaign.
                          </p>
                        )}
                      {estimatedPromoCost > 0 &&
                        Number(newPromo.budget_total_jod || 0) + 0.01 < estimatedPromoCost &&
                        !(
                          selectedMarketingPlan.monthlyBudget != null &&
                          estimatedPromoCost > selectedMarketingPlan.monthlyBudget
                        ) && (
                          <p className="text-xs text-red-700 mt-1">
                            {Number(newPromo.budget_total_jod || 0).toFixed(2)} JOD is below the
                            required {estimatedPromoCost.toFixed(2)} JOD for these dates.
                          </p>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                      {editingPromoId && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingPromoId(null);
                            setNewPromo(emptyPromoForm(newPromo.promotion_type));
                          }}
                          className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold"
                        >
                          Cancel edit
                        </button>
                      )}
                      <button
                        type="submit"
                        className="px-5 py-2.5 bg-amber-700 text-white rounded-lg hover:bg-amber-800 text-sm font-semibold shadow-sm"
                      >
                        {editingPromoId
                          ? newPromo.promotion_type === 'offer'
                            ? 'Save offer'
                            : 'Save campaign'
                          : newPromo.promotion_type === 'offer'
                            ? 'Launch Offer'
                            : 'Launch Campaign'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div id="mkt-promotions-list" className="bg-white rounded-lg shadow-md p-6 scroll-mt-24">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Promotions for {selectedCafe ? 'Selected Cafe' : 'All Cafes'}</h3>
                <p className="text-xs text-gray-500 font-normal">Newest first</p>
              </div>
              {promoLoading && <span className="text-xs text-gray-500">Loading…</span>}
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {([
                { id: 'all', label: 'All' },
                { id: 'active', label: 'Active' },
                { id: 'campaign', label: 'Campaigns' },
                { id: 'offer', label: 'Offers' },
              ] as const).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPromotionListFilter(opt.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    promotionListFilter === opt.id
                      ? 'border-amber-600 bg-amber-600 text-white'
                      : 'border-amber-200 bg-amber-50/50 text-amber-800 hover:bg-amber-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              <span className="ml-1 text-xs text-gray-500">
                Showing {filteredPromotions.length} of {promotions.length}
              </span>
            </div>
            {filteredPromotions.length === 0 && !promoLoading ? (
              <p className="text-sm text-gray-600">
                {promotions.length === 0
                  ? 'No promotions yet. Create your first one above.'
                  : 'No promotions match this filter.'}
              </p>
            ) : (
              <div className="space-y-3">
                {filteredPromotions.map(promo => {
                  const now = new Date();
                  const start = new Date(promo.valid_from);
                  const end = new Date(promo.valid_until);
                  const isRunning = promo.active && start <= now && end >= now;
                  const isScheduled = promo.active && start > now;
                  const isExpired = end < now;

                  const statusLabel = isRunning
                    ? 'Running'
                    : isScheduled
                    ? 'Scheduled'
                    : isExpired
                    ? 'Expired'
                    : 'Paused';

                  const statusClass = isRunning
                    ? 'bg-green-100 text-green-800'
                    : isScheduled
                    ? 'bg-blue-100 text-blue-800'
                    : isExpired
                    ? 'bg-gray-200 text-gray-700'
                    : 'bg-yellow-50 text-yellow-800';

                  return (
                    <div
                      key={promo.id}
                      className="border rounded-lg p-3 flex items-start justify-between"
                    >
                        <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-amber-800">{promo.title}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">{promo.description}</p>
                        <p className="text-xs text-gray-500 mb-1">
                          {(promo.promotion_type || 'campaign') === 'offer'
                            ? `Discount: ${formatPromotionDealLabel(promo)}`
                            : `Ad: ${
                                promo.campaign_placement === 'homepage' ? 'Featured café' : 'Sponsored'
                              } · ${
                                promo.campaign_audience === 'new'
                                  ? 'New customers'
                                  : promo.campaign_audience === 'returning'
                                    ? 'Returning customers'
                                    : 'All customers'
                              }`}
                        </p>
                        <p className="text-xs text-gray-500 mb-1">
                          Total budget:{' '}
                          <span className="font-semibold text-amber-800">
                            {Number(promo.budget_total_jod || 0) > 0
                              ? `${Number(promo.budget_total_jod).toFixed(2)} JOD`
                              : '—'}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mb-1">
                          Type: {(promo.promotion_type || 'campaign') === 'offer' ? 'Offer' : 'Campaign'}
                          {(promo.promotion_type || 'campaign') === 'offer' && (
                            <>
                              {' '}
                              (
                              {promo.offer_tier === 'highlighted'
                                ? OFFER_TIER_OPTIONS.highlighted.label
                                : OFFER_TIER_OPTIONS.basic.label}
                              )
                            </>
                          )}
                        </p>
                        {(promo.promotion_type || 'campaign') === 'offer' && (
                          <p className="text-xs text-gray-500 mb-1">
                            Offer setup fee: {Number(promo.setup_fee_monthly || 0).toFixed(2)} JOD / month
                          </p>
                        )}
                        {(promo.promotion_type || 'campaign') === 'campaign' && Number(promo.performance_addon_enabled || 0) === 1 && (
                          <p className="text-xs text-gray-500 mb-1">
                            Performance add-on: +{Number(promo.performance_fee_per_redeemed_order || 0).toFixed(2)} JOD per redeemed order
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {new Date(promo.valid_from).toLocaleString()} →{' '}
                          {new Date(promo.valid_until).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Daily hours: {formatDailyHoursLabel(promo)}
                        </p>
                      </div>
                        <div className="flex flex-col items-end gap-2">
                          {(typeof promo.orders_count === 'number' || typeof promo.total_revenue === 'number') && (
                            <div className="text-right text-xs text-gray-500">
                              {typeof promo.orders_count === 'number' && (
                                <p>Orders during campaign: {promo.orders_count}</p>
                              )}
                              {typeof promo.total_revenue === 'number' && (
                                <p>Revenue: {formatCurrency(promo.total_revenue)}</p>
                              )}
                            </div>
                          )}
                        <button
                          type="button"
                          onClick={() => {
                            if (promo.cafe_id) setSelectedCafe(promo.cafe_id);
                            setEditingPromoId(promo.id);
                            setNewPromo(promoFormFromRow(promo));
                            setActiveNav('marketing');
                            scrollToPageTarget('mkt-create-promo');
                          }}
                          className="px-3 py-1 rounded-full text-xs font-semibold border border-amber-500 text-amber-700 hover:bg-amber-50"
                        >
                          {editingPromoId === promo.id ? 'Editing' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const newActive = !promo.active;
                              const response = await axios.put(`/api/promotions/${promo.id}/active`, {
                                active: newActive,
                              });
                              const updated = response.data;
                              setPromotions(prev =>
                                prev.map(p => (p.id === promo.id ? updated : p))
                              );
                              fetchCampaignUsage();
                              showToast(
                                newActive
                                  ? 'Campaign activated'
                                  : 'Campaign paused',
                                'success'
                              );
                            } catch (error: any) {
                              console.error('Error updating promotion:', error);
                              showToast(
                                error.response?.data?.error || 'Failed to update campaign',
                                'error'
                              );
                              if (error.response?.status === 403) {
                                fetchCampaignUsage();
                              }
                            }
                          }}
                          className="px-3 py-1 rounded-full text-xs font-semibold border border-amber-500 text-amber-700 hover:bg-amber-50"
                        >
                          {promo.active ? 'Pause' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!cancelOrderInfo}
        title="Cancel Order"
        message="Are you sure you want to cancel this order? This action cannot be undone."
        confirmText="Cancel Order"
        cancelText="Keep Order"
        type="danger"
        onConfirm={confirmCancelOrder}
        onCancel={() => setCancelOrderInfo(null)}
      />
        </div>
      </main>
    </div>
  );
}
